import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, sheetThumbUrl } from "@/lib/api";
import { PIN_CATEGORY } from "@/lib/utils";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import SheetCanvas, { dispatchCanvasCmd } from "@/components/SheetCanvas";
import useSheetOverlayMarkers from "@/components/SheetOverlayMarkers";
import useSheetAssetMarkers from "@/components/SheetAssetMarkers";
import AssetToolPalette from "@/components/AssetToolPalette";
import InternalBadge from "@/components/InternalBadge";
import PinQuickActions from "@/components/PinQuickActions";
import SheetJumpOverlay from "@/components/SheetJumpOverlay";
import GpsPanel, { GpsLocationLayer } from "@/components/GpsOverlay";
import MeasureControls, { MeasureSvgLayer, MeasurementsLayer, useSheetScaleRegions, RegionBoxesLayer } from "@/components/MeasureTool";
import MeasureMagnifier from "@/components/viewer/MeasureMagnifier";
import SnapIndicator from "@/components/viewer/SnapIndicator";
import { useSavedMeasurements } from "@/pages/viewer/hooks/useSavedMeasurements";
import { buildSnapTargets, findSnap } from "@/lib/measureSnap";
import { resolveScaleForPoints } from "@/lib/scaleResolution";
import CropControls, { CropSelectionLayer } from "@/components/CropTool";
import { SearchTrigger } from "@/components/search/CommandPalette";
import MarkupControls, {
  MarkupSvgLayer, useMarkups, useMarkupInteraction, TextEntryPopover, MarkupReadBanner,
} from "@/components/MarkupTool";
import DensityScrubStrip from "@/components/DensityScrubStrip";
import LocateControl from "@/components/LocateControl";
import ProjectLocate from "@/components/ProjectLocate";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";
import { pushRecentSheet, getRecentSheets } from "@/lib/recentSheets";
import { enqueue as offlineEnqueue } from "@/lib/offlineQueueV2";
import { QUICK_PIN_TEMPLATES, partitionTemplatesByRecent, recordTemplateUse } from "@/lib/quickPinTemplates";
import { useTileManager } from "@/components/viewer/TileManager";
import { InspectorProvider, useInspector } from "@/components/viewer/InspectorBridge";
import InspectorBridge from "@/components/viewer/InspectorBridge";
import SheetInspectorPanel from "@/components/viewer/SheetInspectorPanel";
import SheetStatusBar from "@/components/viewer/SheetStatusBar";
import SheetToolRail from "@/components/viewer/SheetToolRail";
import UtilityOverlay from "@/components/viewer/UtilityOverlay";
import ObjectCardOverlay from "@/components/viewer/ObjectCardOverlay";
import OfflineQueuePanel from "@/components/viewer/OfflineQueuePanel";
import ShareModal from "@/components/viewer/ShareModal";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import PlanExportModal from "@/components/viewer/PlanExportModal";
import RasterHealthBanner from "@/components/viewer/RasterHealthBanner";
import { TrustBar } from "@/components/TrustBar";
import { MeasurementHistoryDrawer } from "@/components/MeasurementHistoryDrawer";
import EarthworkEstimatePanel from "@/components/EarthworkEstimatePanel";
import { workspace } from "@/lib/workspace";
import { markStart, markEnd } from "@/lib/perfTelemetry";
import { useOnlineStatus } from "@/lib/useOnlineStatus";
import { AckPill } from "@/components/AckPill";
import {
  ArrowLeft, ZoomIn, ZoomOut, Maximize2, Plus, X, Smartphone, PanelRight, Navigation, Hash, Ruler, Crop, Pencil, PenSquare, ChevronLeft, ChevronRight, AlertTriangle, GitBranch, MoreHorizontal, Share2, FileDown, Compass, History as HistoryIcon, Mountain, Star, RefreshCw, Copy,
} from "lucide-react";
import NeedsReviewBadge from "@/components/NeedsReviewBadge";
import { useAuth } from "@/contexts/AuthContext";
import { canAuthorPipes } from "@/lib/permissions";
import { trackViewerEvent } from "@/lib/viewerTelemetry";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TouchTooltip from "@/components/TouchTooltip";
import { DISCIPLINE_LABEL } from "@/lib/utils";
import { useMeasurementController } from "@/pages/viewer/hooks/useMeasurementController";
import { useGpsController } from "@/pages/viewer/hooks/useGpsController";
import { useAckController } from "@/pages/viewer/hooks/useAckController";
import { useViewerShortcuts } from "@/pages/viewer/hooks/useViewerShortcuts";
import UtilityIntelLayer from "@/components/viewer/UtilityIntelLayer";
import EphemeralCrossingInspector from "@/components/viewer/EphemeralCrossingInspector";
import OperationalStatusSpine from "@/components/viewer/OperationalStatusSpine";
import RevisionImpactStationsBanner from "@/components/viewer/RevisionImpactStationsBanner";
import MeasurementStationOverlay from "@/components/viewer/MeasurementStationOverlay";
import PipeLayer from "@/components/viewer/PipeLayer";
import { readUtilityIntelVisible, writeUtilityIntelVisible } from "@/lib/utilityIntel";

/**
 * SheetViewer — the actual product.
 *
 * Renders a single sheet as a backend-rendered JPEG raster inside the
 * smooth pan/zoom SheetCanvas. The drawing dominates the screen; chrome
 * is intentionally thin and gets out of the way.
 *
 * Field Mode: full immersion. No top bar. No sidebar. Just the drawing,
 * a floating Drop Pin FAB, a sheet quick-switch pill, and Quick Pin
 * template chips when adding.
 */
export default function SheetViewer() {
  // Outer wrapper — provides InspectorProvider so SheetViewerBody can call
  // useInspector() to wire the LayerOverlay tap → inspector flow.
  return (
    <InspectorProvider>
      <SheetViewerBody />
    </InspectorProvider>
  );
}


function SheetViewerBody() {
  // Inspector wiring (Iter 31) — LayerOverlay tap-on-structure routes
  // through useInspector to open the structure panel. Drawing stays
  // dominant; inspector slides in from the right.
  const inspector = useInspector();
  const focusedStructureId = inspector.state?.kind === "structure" ? inspector.state.id : null;

  // Iter 51 — Add Pipe mode. Tap structure A → tap structure B → server
  // creates pipe with auto-derived A→B geometry + station snapshot.
  const [addPipeMode, setAddPipeMode] = useState(false);
  const [pipeFromStruct, setPipeFromStruct] = useState(null);
  const [pipeReloadKey, setPipeReloadKey] = useState(0);
  const [pipeBusy, setPipeBusy] = useState(false);
  const [pipeError, setPipeError] = useState(null);

  const onStructurePick = async (structure) => {
    if (addPipeMode) {
      // First tap → remember the from-structure
      if (!pipeFromStruct) {
        setPipeFromStruct(structure);
        return;
      }
      // Second tap → create the pipe
      if (pipeFromStruct.id === structure.id) {
        setPipeError("Pick a different structure for the pipe's end.");
        return;
      }
      setPipeBusy(true); setPipeError(null);
      try {
        const { createPipe } = await import("@/lib/pipes");
        const pipe = await createPipe(sheetId, {
          project_id: sheet?.project_id,
          from_structure_id: pipeFromStruct.id,
          to_structure_id: structure.id,
        });
        setPipeFromStruct(null);
        setAddPipeMode(false);
        setPipeReloadKey((k) => k + 1);
        inspector.open({ kind: "pipe", id: pipe.id, payload: pipe });
      } catch (e) {
        setPipeError(e?.response?.data?.detail || e?.message || "Failed to create pipe");
      } finally {
        setPipeBusy(false);
      }
      return;
    }
    inspector.open({ kind: "structure", id: structure.id, payload: structure });
  };

  const { sheetId, projectId: routeProjectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // R11 continuation (2026-09-10) — consolidated jump-target resolver.
  // Previously each of the 7 addressable object types repeated its own
  // near-identical lookup+open block, twice (live jump + post-navigation
  // replay), with every failure silently swallowed — a deleted/
  // superseded/unauthorized related object just did nothing, which is
  // NOT "handled clearly". One function now owns: the lookup-per-type
  // dispatch table, opening the right inspector on success, and a
  // user-visible toast distinguishing "no access" (401/403) from
  // "missing/deleted/superseded" (404 or not found in the list) on
  // failure. `pin` has no separate lookup — it already has a live
  // marker + quick-actions popover on this sheet, so jumping just
  // opens that popover directly (setActivePin).
  const openInspectorForJump = useCallback((t) => {
    const objectType = t.object_type;
    const objectId = t.object_id;
    if (!objectType || !objectId) return;
    const targetSheetId = t.sheet_id || sheetId;
    const label = t.label || objectType.replace(/_/g, " ");

    if (objectType === "pin") { setActivePin(objectId); return; }

    const lookups = {
      alignment: () => api.get(`/alignments/${objectId}`).then(({ data }) => data),
      pipe: () => api.get(`/pipes/${objectId}`).then(({ data }) => data),
      conflict: () => api.get(`/utility_conflicts/${objectId}`).then(({ data }) => data),
      structure: () => api.get(`/sheets/${targetSheetId}/structures`)
        .then(({ data }) => (data || []).find((s) => s.id === objectId)),
      measurement: () => api.get(`/sheets/${targetSheetId}/measurements`)
        .then(({ data }) => (Array.isArray(data) ? data : data?.measurements || []).find((m) => m.id === objectId)),
      utility_watch: () => api.get("/utility_watches", { params: { sheet_id: targetSheetId } })
        .then(({ data }) => (data?.watches || []).find((w) => w.id === objectId)),
    };
    const lookup = lookups[objectType];
    // e.g. "revision" / "photo" / "issue" — landing on the sheet IS the
    // whole jump target; there's no dedicated per-object inspector yet.
    if (!lookup) return;

    lookup().then((payload) => {
      if (!payload) {
        toast.error(`"${label}" may have been deleted or superseded — couldn't open it.`);
        return;
      }
      inspector.open({ kind: objectType, id: objectId, payload });
    }).catch((err) => {
      const status = err?.response?.status;
      toast.error(status === 401 || status === 403
        ? `You don't have access to "${label}".`
        : `"${label}" may have been deleted or moved — couldn't open it.`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  // Iter 48 — Jump-To listener. Dispatched by ObjectRelations / Search.
  // Same-sheet jumps: drop a `jumpTarget` state for future camera animation.
  // Cross-sheet jumps: navigate to the target sheet route + persist the
  // jump target so the new viewer can pick it up post-mount.
  const [jumpTarget, setJumpTarget] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const t = e.detail || {};
      if (!t.object_type) return;
      if (t.sheet_id && t.sheet_id !== sheetId) {
        try {
          sessionStorage.setItem("fop_jump_target", JSON.stringify(t));
        } catch { /* ignore */ }
        navigate(`/sheet/${t.sheet_id}`);
        return;
      }
      setJumpTarget(t);
      openInspectorForJump(t);
      // Iter 52 — forward target_tab hint to the ObjectInspector.
      if (t.target_tab && t.object_type && t.object_id) {
        // Fire after the inspector mounts.
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent("forgedops:inspector-tab", {
            detail: {
              object_type: t.object_type,
              object_id: t.object_id,
              tab: t.target_tab,
              focus_document_id: t.focus_document_id,
            },
          }));
        }, 250);
      }
      // Auto-clear the marker so the highlight is transient.
      window.setTimeout(() => setJumpTarget((cur) => cur === t ? null : cur), 3500);
    };
    window.addEventListener("forgedops:jump", handler);
    return () => window.removeEventListener("forgedops:jump", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId, navigate, openInspectorForJump]);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("fop_jump_target");
      if (!raw) return;
      const t = JSON.parse(raw);
      if (t && t.sheet_id === sheetId) {
        setJumpTarget(t);
        openInspectorForJump(t);
        // Iter 53.1 — replay target_tab on post-mount jumps too so a
        // cross-sheet "open SMH-XX docs" deep link lands on the right
        // tab on the new sheet.
        if (t.target_tab && t.object_type && t.object_id) {
          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent("forgedops:inspector-tab", {
              detail: {
                object_type: t.object_type,
                object_id: t.object_id,
                tab: t.target_tab,
                focus_document_id: t.focus_document_id,
              },
            }));
          }, 400);
        }
        window.setTimeout(() => setJumpTarget(null), 3500);
      }
      sessionStorage.removeItem("fop_jump_target");
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId, openInspectorForJump]);
  const { user } = useAuth();
  const canEditMetadata = !!user && ["admin", "owner", "project_manager"].includes(user.role);
  const [sheet, setSheet] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [project, setProject] = useState(null);
  const [pins, setPins] = useState([]);
  const [users, setUsers] = useState([]);
  const [activePin, setActivePin] = useState(null);
  const [addMode, setAddMode] = useState(false);
  // Iter 76 — share modal visibility.
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [allSheets, setAllSheets] = useState([]);
  const [newPinPos, setNewPinPos] = useState(null);   // {nx, ny} normalized
  const [template, setTemplate] = useState(null);
  const [scaleHint, setScaleHint] = useState(1);
  // Track 1 — Sheet Overlay Markers. Reads /sheets/:id/field-timeline +
  // emits zoom-aware clustered pins that merge into the existing pin
  // pipeline. iPad touch handlers in SheetCanvas remain untouched.
  const overlayMarkers = useSheetOverlayMarkers({
    sheetId: sheet?.id || null,
    projectId: sheet?.project_id || null,
    scale: scaleHint,
  });
  // Track 4 — Asset markers (placed real-world objects).
  const assetMarkers = useSheetAssetMarkers({
    sheetId: sheet?.id || null,
    projectId: sheet?.project_id || null,
    scale: scaleHint,
  });
  const online = useOnlineStatus();
  // Iter 73 Phase 6 — global layer visibility, broadcast from LayersPanel.
  const [layerVisibility, setLayerVisibility] = useState({});
  useEffect(() => {
    const h = (e) => setLayerVisibility(e?.detail || {});
    window.addEventListener("forgedops:layer-visibility", h);
    return () => window.removeEventListener("forgedops:layer-visibility", h);
  }, []);
  // Iter 144 — Touch-first default for field deployment. Field workers
  // on iPads should LAND in Field Mode (drawing-dominant, no chrome) and
  // with the inspector sidebar collapsed. Desktop users keep the full
  // chrome. Explicit user preference (via the Field-Mode toggle button)
  // is honored if set. Detection: coarse pointer OR iPadOS userAgent
  // (iPadOS Safari reports as Macintosh + maxTouchPoints>1).
  const isTouchPrimary = (() => {
    try {
      if (typeof window === "undefined") return false;
      const ua = navigator.userAgent || "";
      const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
      const ipadOS = /iPad|iPhone|iPod/.test(ua) ||
                     (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
      return !!coarse || !!ipadOS;
    } catch { return false; }
  })();
  const [fieldMode, setFieldMode] = useState(() => {
    try {
      const stored = localStorage.getItem("fop_field_mode");
      if (stored === "1") return true;
      if (stored === "0") return false;
      // No explicit preference — iPad/touch devices default to Field Mode.
      return isTouchPrimary;
    } catch { return isTouchPrimary; }
  });
  // PILLAR 2 — Measurement History Drawer state.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [earthworkOpen, setEarthworkOpen] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [favBusy, setFavBusy] = useState(false);

  // Wave 6 (2026-09-11) — load this user's star state whenever the sheet changes.
  useEffect(() => {
    if (!sheet?.id) return;
    api.get(`/sheets/${sheet.id}/favorite`)
      .then((r) => setFavorited(!!r.data?.favorited))
      .catch(() => setFavorited(false));
  }, [sheet?.id]);

  const toggleFavorite = async () => {
    if (!sheet?.id || favBusy) return;
    setFavBusy(true);
    const next = !favorited;
    setFavorited(next); // optimistic
    try {
      if (next) {
        await api.post(`/sheets/${sheet.id}/favorite`);
      } else {
        await api.delete(`/sheets/${sheet.id}/favorite`);
      }
    } catch {
      setFavorited(!next); // revert on failure
    } finally {
      setFavBusy(false);
    }
  };
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("fop_sidebar");
      if (stored === "1") return true;
      if (stored === "0") return false;
      // No explicit preference — iPad/touch devices land with sidebar closed
      // so the drawing has the full screen on first open.
      return !isTouchPrimary;
    } catch { return !isTouchPrimary; }
  });
  const [recentList, setRecentList] = useState(() => getRecentSheets(sheetId));

  // Iter 67 Phase 4 — Revision visibility. Fetch revision_status so the
  // REV pill can show LATEST or N BEHIND. Cheap one-shot per sheet.
  const [revStatus, setRevStatus] = useState(null);
  // GH-10 (real-plan certification, 2026-09-30) — distinct from "haven't
  // checked yet" (null + no error): an explicit failed-fetch marker so
  // the UI can show "unknown/retry" instead of silently keeping whatever
  // the PREVIOUS sheet's status happened to be.
  const [revStatusError, setRevStatusError] = useState(false);
  const [revStatusRetryNonce, setRevStatusRetryNonce] = useState(0);
  // SC-4 — project-aware Locate ("Where am I?") overlay state.
  const [projectLocateOpen, setProjectLocateOpen] = useState(false);
  // Iter 178.4 — Asset placement palette now lives in the left rail.
  // Parent owns open/close so the rail button can reflect the toggle
  // state as an active indicator.
  const [assetPaletteOpen, setAssetPaletteOpen] = useState(false);
  // Project-coord position to draw on canvas (from GPS) or null.
  // {nx, ny, accuracy_in_units, horizontal_units_label, confidence, kind}
  const [projectPosition, setProjectPosition] = useState(null);
  // Manual "I'm Here" capture mode — next canvas tap records.
  const [imHereMode, setImHereMode] = useState(false);
  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    // GH-10 — reset to "unknown" SYNCHRONOUSLY, before the new sheet's
    // fetch even starts. Reproduced defect: revStatus kept whatever the
    // PREVIOUS sheet's value was (e.g. "2 revisions behind") until/unless
    // the new fetch happened to succeed; a failed fetch left it there
    // forever, wrongly showing SUPERSEDED on a sheet that is actually
    // current (or vice versa). "Unknown" (null, no banner) is the only
    // safe default — never infer latest-ness from the previous sheet's
    // stale state.
    setRevStatus(null);
    setRevStatusError(false);
    (async () => {
      try {
        const r = await api.get(`/sheets/${sheetId}/revision_status`);
        if (!cancelled) setRevStatus(r.data);
      } catch {
        if (!cancelled) setRevStatusError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [sheetId, revStatusRetryNonce]);

  // ===== Iter 32 — Field Acknowledgment Loop =====
  // Extracted to useAckController (Iter 36.5). Owns ack_summary +
  // active review + my_ack + deep-link auto-focus.
  const deepLinkReviewId = searchParams.get("review");
  const deepLinkFocus = searchParams.get("focus"); // structure label
  const {
    ackSummary, activeReview, myAck, onAckResolved, focusFirstImpact,
  } = useAckController({ sheetId, deepLinkReviewId, deepLinkFocus, inspector });

  // Scrub-strip handshake pulse state. `scrubPulseTick` is whatever opaque
  // value drives a fresh pulse on the active cell; `scrubPulseSeverity` is
  // the colour tint (null = orange default for sheet changes; pin priority
  // for pin activations — strictly momentary, never persisted on cells).
  const [scrubPulseTick, setScrubPulseTick] = useState(sheetId);
  const [scrubPulseSeverity, setScrubPulseSeverity] = useState(null);
  // Pulse on every sheet change.
  useEffect(() => {
    setScrubPulseTick(sheetId);
    setScrubPulseSeverity(null);
  }, [sheetId]);
  // Pulse on every pin activation — coloured by pin priority.
  useEffect(() => {
    if (!activePin) return;
    const pin = pins.find((p) => p.id === activePin);
    if (!pin) return;
    setScrubPulseTick(`${sheetId}|${activePin}|${Date.now()}`);
    setScrubPulseSeverity(pin.priority || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePin]);

  // Iter 144 Phase 2 — precision magnifier screen position (clientX/Y).
  // Declared with a placeholder until measureMode is in scope; the reset
  // effect below uses measureMode (after destructuring from the controller).
  const [measureScreenPos, setMeasureScreenPos] = useState(null);
  // TRACK 1 — Precision-loupe long-press gate.
  const [precisionHold, setPrecisionHold] = useState(false);

  // GPS — extracted to useGpsController (Iter 36.5). Owns calibration,
  // live position, transform, locate/follow/drop, distance-from-me.
  const gps = useGpsController({ sheetId });
  const {
    cal, saveCalibration, clearCalibration, gpsTransform, gpsReady,
    livePosition, liveError, gpsEnabled, toggleGpsLive,
    gpsPanelOpen, setGpsPanelOpen,
    followMode, dropMode, setDropMode, setFollowMode,
    manualPosition, setManualPosition,
    awaitingAnchorTap, setAwaitingAnchorTap,
    anchorTapPos, setAnchorTapPos,
    handleLocateMe, handleToggleFollow, handleToggleDropMode,
    distanceFromMe,
  } = gps;

  // Sheet-jump overlay.
  const [jumpOpen, setJumpOpen] = useState(false);

  // Iter 37 — Utility Intelligence V1. visibility persisted; default ON
  // (per directive "Within 10s a super can identify areas deserving
  // attention" — needs to be visible on first open). activeCrossing
  // drives the ConflictInspector popover. Tapping a diamond opens it.
  const [utilityIntelVisible, setUtilityIntelVisible] = useState(() => readUtilityIntelVisible());
  const [activeCrossing, setActiveCrossing] = useState(null);
  useEffect(() => { setActiveCrossing(null); }, [sheetId]);

  // SC-4 — re-hydrate the most-recent manual ("I'm Here") position when
  // the sheet loads. The in-session record path already populates the
  // marker from the modal callback — this fills the gap so a previously
  // dropped pin shows on reload (per testing-agent action item).
  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/sheets/${sheetId}/manual-position`);
        if (cancelled) return;
        if (data && data.kind === "manual" && typeof data.nx === "number") {
          setProjectPosition({
            nx: data.nx, ny: data.ny,
            kind: "manual", on_current_sheet: true,
            confidence: "low",
            accuracy_in_units: null,
            horizontal_units_label: "ft",
          });
        } else {
          // Either no record or fresh sheet — clear stale marker from prior sheet.
          setProjectPosition(null);
        }
      } catch {
        if (!cancelled) setProjectPosition(null);
      }
    })();
    return () => { cancelled = true; };
  }, [sheetId]);
  const toggleUtilityIntel = useCallback(() => {
    setUtilityIntelVisible((v) => {
      const next = !v;
      writeUtilityIntelVisible(next);
      if (!next) setActiveCrossing(null);
      return next;
    });
  }, []);

  // Measurement — extracted to useMeasurementController (Iter 36.5).
  // Owns mode/toolMode/pts/hover/scale/reloadKey + 4-mode click router +
  // offline-enqueue auto-save + undo.
  // Wave B · Turn 1 real-plan cert HIGH fix — canonical raster pixel
  // dims for aspect-ratio-correct geometry. Angle needs these even
  // without a scale; sourced from the SAME <img> SheetCanvas paints.
  const [viewerImgDims, setViewerImgDims] = useState(null);
  // R-12 (independent recheck batch 2, 2026-10-01) — declared BEFORE
  // `useMeasurementController` and passed in below as the controller's
  // ONLY source of region-calibration data, instead of the controller
  // independently re-fetching the exact same `/sheets/{id}/scales`
  // list into a second, never-invalidated-by-this-one copy. See the
  // comment in `useMeasurementController.js` for the full defect.
  const scaleRegions = useSheetScaleRegions(sheetId);
  const measure = useMeasurementController({ sheetId, gpsTransform, imgDims: viewerImgDims, scaleContext: sheet?.scale_context, identityKey: user ? `${user.tenant_id}:${user.id}` : null, scaleRegions: scaleRegions.regions });

  // R11 continuation (2026-09-10) — precise, stable camera navigation.
  // SheetCanvas already exposes a canonical `centerOn(nx, ny)` (IP-002
  // FOCUS_OBJECT, preserves scale) via the `fop_canvas_cmd` window
  // event — it existed for a future GPS "Locate Me" but was never
  // actually dispatched from anywhere. Reuse it here for jump targets.
  // Only fires once the raster's REAL pixel dims are known (never on
  // the {w:1,h:1} placeholder) so it can't compute garbage, and only
  // once per distinct jump instance — a second, faster jump supersedes
  // a slower one instead of both fighting; once applied, it never
  // re-fires and so never fights the operator's own pan/zoom afterward.
  const appliedFocusRef = useRef(null);
  useEffect(() => {
    if (!jumpTarget) return;
    if (jumpTarget.normalized_x == null || jumpTarget.normalized_y == null) return;
    if (!viewerImgDims || viewerImgDims.w <= 1) return; // geometry not ready yet
    if (appliedFocusRef.current === jumpTarget) return; // already centered for this exact jump instance
    appliedFocusRef.current = jumpTarget;
    dispatchCanvasCmd(sheetId, "centerOn", { nx: jumpTarget.normalized_x, ny: jumpTarget.normalized_y });
  }, [jumpTarget, viewerImgDims, sheetId]);
  const {
    measureMode, toolMode, measurePts, measureHover, sheetScale,
    measurementsReloadKey,
    startMeasure, startCalibrate, startRegionBox, closeMeasure,
    onMeasureClick, onMeasureHover, undoMeasurePoint,
    setToolMode, clearPoints, saveSheetScale, clearSheetScale,
  } = measure;

  // Connected-workflow program (2026-09-10) — Detail Region calibration.
  // Step 1 (measureMode="region_box"): 2 opposite-corner taps define the
  // region's bbox. Step 2: once drawn, the SAME 2-tap "calibrate" flow
  // (unchanged) runs scoped to that box; MeasureControls posts to
  // /sheets/{id}/scales instead of PUT /sheets/{id}/scale.
  const [pendingRegionBbox, setPendingRegionBbox] = useState(null);
  useEffect(() => {
    if (measureMode !== "region_box" || measurePts.length !== 2) return;
    const [a, b] = measurePts;
    const bbox = [
      Math.min(a.nx, b.nx), Math.min(a.ny, b.ny),
      Math.max(a.nx, b.nx), Math.max(a.ny, b.ny),
    ];
    if (bbox[2] - bbox[0] < 0.01 || bbox[3] - bbox[1] < 0.01) {
      toast.error("That box is too small — draw a larger detail region.");
      clearPoints();
      return;
    }
    setPendingRegionBbox(bbox);
    startCalibrate();
  }, [measureMode, measurePts, clearPoints, startCalibrate]);
  const onRegionCalibrateSave = useCallback(async (payload) => {
    const result = await scaleRegions.create(payload);
    setPendingRegionBbox(null);
    return result;
  }, [scaleRegions]);
  const onCancelRegion = useCallback(() => setPendingRegionBbox(null), []);
  // Defensive reset: if the operator abandons the flow entirely (closes the
  // tool, jumps straight to plain measuring) without hitting Cancel/Save,
  // a stale bbox must never leak into a later, unrelated calibration.
  useEffect(() => {
    if (measureMode !== "calibrate" && measureMode !== "region_box") setPendingRegionBbox(null);
  }, [measureMode]);

  // Iter 144 Phase 2 — clear the precision magnifier's screen position
  // whenever measurement mode turns off, so a stale finger position from
  // the last session doesn't ghost a loupe on the next entry into the tool.
  useEffect(() => {
    if (measureMode === "off") setMeasureScreenPos(null);
  }, [measureMode]);

  // Iter 145 — CAD snap engine.
  // Iter 146 — Snap + magnifier are DISABLED on touch primary devices
  // (iPad/iPhone). Operator field-test reported flicker and "random
  // zoom" symptoms on real iPad; until the snap state machine has
  // proper hysteresis + pointercancel debouncing, simple-and-reliable
  // beats clever-and-glitchy. Desktop users keep the full snap UX.
  const snapEnabled = !isTouchPrimary;
  const { rows: savedMeasurements, stale: savedMeasurementsStale } = useSavedMeasurements(sheetId, measurementsReloadKey);
  // GH-13 — a failed measurements read must never look like "no saved
  // measurements" with no explanation. Surface it once per occurrence;
  // the hook itself already preserved the last confirmed rows.
  useEffect(() => {
    if (savedMeasurementsStale) {
      toast.warning("Couldn't refresh saved measurements — showing the last saved version.", {
        duration: 5000,
        id: `measurements-stale-${sheetId}`,
      });
    }
  }, [savedMeasurementsStale, sheetId]);
  const snapTargets = useMemo(
    () => (snapEnabled ? buildSnapTargets({ sheetScale, measurePts, savedMeasurements }) : []),
    [snapEnabled, sheetScale, measurePts, savedMeasurements],
  );
  const currentSnap = useMemo(
    () => (snapEnabled && measureMode !== "off" ? findSnap(measureHover, snapTargets) : null),
    [snapEnabled, measureMode, measureHover, snapTargets],
  );
  // Haptic feedback — one short pulse when snap target changes. Gracefully
  // no-ops on devices without navigator.vibrate (most desktops, all iOS).
  const lastSnapKeyRef = useRef(null);
  // TRACK 6 — pan/zoom frame-interval tracker for the perf telemetry.
  const panZoomLastTickRef = useRef(null);
  useEffect(() => {
    const key = currentSnap ? `${currentSnap.kind}:${currentSnap.nx.toFixed(4)}:${currentSnap.ny.toFixed(4)}` : null;
    if (key !== lastSnapKeyRef.current) {
      lastSnapKeyRef.current = key;
      if (key && typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        try { navigator.vibrate(8); } catch { /* silent */ }
      }
    }
  }, [currentSnap]);
  // Effective hover point — replaced by the snap target when one is found.
  // The MeasureSvgLayer rubber-band, the live distance label, AND the
  // magnifier all read this so they remain mutually consistent.
  const snappedHover = useMemo(() => {
    if (!measureHover) return null;
    if (currentSnap) return { nx: currentSnap.nx, ny: currentSnap.ny };
    return measureHover;
  }, [measureHover, currentSnap]);
  // GH-22 (real-plan certification, 2026-10-01) — the on-canvas LIVE
  // measurement readout (`MeasureSvgLayer`'s own internal distance/
  // area/arc calculations) was always given the whole-sheet
  // `sheetScale`, bypassing region calibration entirely — a DIFFERENT
  // call site from (but the same root cause as) the one fixed in
  // `useMeasurementController`'s click handler. Resolve against
  // whatever points are visible right now (committed taps + the
  // current hover/drag point) so what's drawn on screen while
  // dragging matches what gets saved a moment later.
  const liveScale = useMemo(() => {
    const livePoints = snappedHover ? [...measurePts, snappedHover] : measurePts;
    if (!livePoints.length) return sheetScale;
    return resolveScaleForPoints(scaleRegions.regions, livePoints, sheet?.scale_context).scale || sheetScale;
  }, [scaleRegions.regions, measurePts, snappedHover, sheet?.scale_context, sheetScale]);
  // Wrap onMeasureClick so the placed point uses the snapped coordinate
  // instead of the raw fingertip pixel. This is the actual "magnet" — the
  // visual ring is informational; without the wrap a snapped indicator
  // would lie about where the next tap lands.
  const onMeasureClickSnapped = useCallback((nx, ny) => {
    const raw = { nx, ny };
    const snap = findSnap(raw, snapTargets);
    if (snap) {
      onMeasureClick(snap.nx, snap.ny);
    } else {
      onMeasureClick(nx, ny);
    }
  }, [snapTargets, onMeasureClick]);

  // Crop tool state.
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [cropIncludePins, setCropIncludePins] = useState(false);
  const [cropIncludeMeasurements, setCropIncludeMeasurements] = useState(false);
  useEffect(() => {
    // Reset crop on sheet change.
    setCropMode(false);
    setCropRect(null);
  }, [sheetId]);
  const openCrop = () => {
    closeAllToolModes();
    setCropMode(true);
    setCropRect(null);
  };
  const closeCrop = () => { setCropMode(false); setCropRect(null); };

  // Markup state.
  const [markupMode, setMarkupMode] = useState(false);
  const markupState = useMarkups(sheetId, sheet?.project_id);
  const markupInteraction = useMarkupInteraction({
    tool: markupState.tool,
    color: markupState.color,
    add: markupState.add,
  });
  useEffect(() => {
    // Reset markup mode on sheet change (markup state is reset by useMarkups already).
    setMarkupMode(false);
  }, [sheetId]);
  const openMarkup = () => {
    closeAllToolModes();
    setMarkupMode(true);
    // Iter 178.9 — Do NOT auto-arm any tool as default. Previously we set
    // `tool = "arrow"` on open, but the tray's tool-button `onClick` at
    // MarkupTool.jsx:401 toggles off when the button matches the current
    // tool. So clicking the arrow button (first thing many users do)
    // silently DEACTIVATED it: `tool === "arrow"` → `setTool(null)`. Every
    // other tool worked because the pre-armed default didn't match theirs.
    // Leaving tool null forces the operator to pick — same UX as Bluebeam.
  };
  const closeMarkup = () => {
    setMarkupMode(false);
    markupState.setTool(null);
    markupInteraction.reset();
  };

  // -------------------------------------------------------------------
  // Iter FIELD-UX-2 — SINGLE TOOL OWNERSHIP MODEL
  //
  // Operator field test on iPad found that touch-capturing tools could
  // coexist: e.g. "I'm Here" stayed armed while Measure was on, so the
  // first measure tap was eaten by the I'm Here handler. Field Position
  // could be active during Markup. The user had no way to tell which
  // tool owned canvas taps.
  //
  // Rule from the directive: ONLY ONE touch-capturing tool may own the
  // canvas at a time. Below is the single chokepoint. Every tool
  // activation flows through `activateTool(name)` which (a) closes
  // every other tool first, then (b) sets the new tool on.
  // -------------------------------------------------------------------
  const closeAllToolModes = useCallback(() => {
    // Measure / Calibrate
    closeMeasure();
    // Markup
    setMarkupMode(false);
    if (markupState.setTool) markupState.setTool(null);
    if (markupInteraction.reset) markupInteraction.reset();
    // Crop / Snip
    setCropMode(false);
    setCropRect(null);
    // Pin placement
    setAddMode(false);
    setNewPinPos(null);
    setTemplate(null);
    // SC-4 manual "I'm Here" + legacy lat/lng drop
    setImHereMode(false);
    if (typeof setDropMode === "function") setDropMode(false);
    // GPS calibration anchor capture
    setAwaitingAnchorTap(false);
    // Utility pipe drawing
    setAddPipeMode(false);
    setPipeFromStruct(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTool = (
    measureMode === "measure"     ? "measure"
    : measureMode === "calibrate" ? "calibrate"
    : markupMode                  ? "markup"
    : cropMode                    ? "crop"
    : imHereMode                  ? "imhere"
    : awaitingAnchorTap           ? "anchor"
    : dropMode                    ? "dropmarker"
    : addMode                     ? "addpin"
    : addPipeMode                 ? "addpipe"
    :                              "pan"
  );

  // Friendly label / instruction pair for the ownership indicator chip.
  const activeToolMeta = {
    measure:    { label: "Measuring",         hint: "Tap two points on the sheet" },
    calibrate:  { label: "Calibrating scale", hint: "Tap two known endpoints" },
    markup:     { label: "Marking up",        hint: "Tap to place a markup" },
    crop:       { label: "Cropping",          hint: "Drag a rectangle" },
    imhere:     { label: "Placing I'm Here",  hint: "Tap your real-world spot on the sheet" },
    anchor:     { label: "Setting anchor",    hint: "Tap the GPS anchor location" },
    dropmarker: { label: "Drop position",     hint: "Tap to drop a manual marker" },
    addpin:     { label: "Adding pin",        hint: "Tap where the pin goes" },
    addpipe:    { label: "Drawing pipe",      hint: "Tap structures to connect" },
    pan:        { label: "Pan",                hint: null },
  }[activeTool] || { label: activeTool, hint: null };

  const cancelActiveTool = useCallback(() => {
    closeAllToolModes();
  }, [closeAllToolModes]);

  // -------------------------------------------------------------------
  // Iter FIELD-UX-2 — exclusive start helpers.
  // Every touch-capturing tool MUST be armed via one of these so the
  // ownership invariant ("at most one active tool") cannot be broken.
  // Direct setState in event handlers is a code-smell from now on.
  // -------------------------------------------------------------------
  const startMeasureExclusive = (mode) => { closeAllToolModes(); startMeasure(mode); };
  const startCalibrateExclusive = () => { closeAllToolModes(); startCalibrate(); };
  const startRegionBoxExclusive = () => { closeAllToolModes(); startRegionBox(); };
  const armImHere = () => { closeAllToolModes(); setImHereMode(true); };
  const armAddPin = () => { closeAllToolModes(); setAddMode(true); };
  const armAddPipe = () => { closeAllToolModes(); setAddPipeMode(true); };
  const armAnchorTap = () => { closeAllToolModes(); setAwaitingAnchorTap(true); };
  const armDropMode = () => { closeAllToolModes(); if (typeof setDropMode === "function") setDropMode(true); };

  const toggleFieldMode = () => {
    setFieldMode((v) => {
      const next = !v;
      try { localStorage.setItem("fop_field_mode", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const toggleSidebar = () => {
    setSidebarOpen((v) => {
      const next = !v;
      try { localStorage.setItem("fop_sidebar", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // GH-05 (real-plan certification, 2026-09-30) — `reload` had no
  // request-generation/cancellation guard. Switching sheets quickly
  // (A then B) starts a second `reload()` before the first's awaits
  // settle; if A's slower response resolves AFTER B's, its `setSheet`/
  // `setProject` calls silently overwrote B's already-displayed state
  // — the route stayed on B while the drawing, heading and revision all
  // reverted to A. `latestRequestedSheetIdRef` always holds the MOST
  // RECENTLY requested sheet; a stale call checks against it (not its
  // own closed-over `sheetId`, which never changes) after every await
  // and abandons without touching any state the instant it's no longer
  // the latest request.
  const latestRequestedSheetIdRef = useRef(null);

  const reload = useCallback(async () => {
    if (!sheetId) return;
    const requestedSheetId = sheetId;
    latestRequestedSheetIdRef.current = requestedSheetId;
    setLoadError(null);
    // TRACK 3 — measure sheet open latency.
    markStart(`sheet_open:${requestedSheetId}`);
    // Wave B · Turn 1 · WP-B1.04 (PLANS-AUDIT-5.8-02) — the sheet itself is
    // required (nothing renders without it), but the pins list and the
    // user directory are supporting data. Promise.all previously meant
    // ANY one of the three failing (e.g. offline with an uncached /users
    // response) crashed the whole load with an unhandled rejection and
    // left the viewer stuck. Promise.allSettled + explicit fallbacks keep
    // the sheet + pins on screen even when a non-critical call fails.
    const [sheetResult, pinsResult, usersResult] = await Promise.allSettled([
      api.get(`/sheets/${requestedSheetId}`),
      api.get(`/sheets/${requestedSheetId}/pins`),
      api.get("/users"),
    ]);
    // GH-05 — a newer sheet has been requested since this call started.
    // Ignore this (now stale) response entirely rather than let it
    // overwrite the newer sheet's already-displayed state.
    if (latestRequestedSheetIdRef.current !== requestedSheetId) return;
    if (sheetResult.status === "rejected") throw sheetResult.reason;
    const s = sheetResult.value.data;
    const p = pinsResult.status === "fulfilled" ? pinsResult.value.data : [];
    const u = usersResult.status === "fulfilled" ? usersResult.value.data : [];
    setSheet(s);
    setPins(p);
    setUsers(u);
    markEnd(`sheet_open:${requestedSheetId}`, "sheet_open");
    // TRACK 3 — persist last sheet for the dashboard Resume card.
    try {
      workspace.setLastSheet({
        sheet_id: s.id,
        project_id: s.project_id,
        sheet_number: s.sheet_number,
        sheet_title: s.sheet_title,
      });
    } catch { /* swallow — best-effort */ }
    if (s?.project_id) {
      try {
        const { data: pj } = await api.get(`/projects/${s.project_id}`);
        // GH-05 — same guard after the second await: a sheet request
        // can go stale between the sheet fetch above and this project
        // fetch too.
        if (latestRequestedSheetIdRef.current !== requestedSheetId) return;
        setProject(pj);
        try {
          workspace.setLastProject({
            project_id: pj.id,
            project_name: pj.name,
          });
        } catch { /* swallow */ }
      } catch {}
    }
  }, [sheetId]);

  useEffect(() => {
    reload().catch((e) => {
      console.error("SheetViewer reload failed:", e);
      setLoadError(e);
    });
  }, [reload]);

  // Iter EXP-2A — Show the "Welcome back, resuming X" toast set by
  // the login flow when the user was redirected here via last-location
  // resume. Fire once per session — clear the sessionStorage marker so
  // route navigations within the same session don't re-toast.
  useEffect(() => {
    try {
      const label = window.sessionStorage.getItem("fop:resume-toast");
      if (label) {
        window.sessionStorage.removeItem("fop:resume-toast");
        toast.success(`Welcome back — resuming ${label}`, { duration: 4000 });
      }
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    if (!sheet) return;
    pushRecentSheet({
      id: sheet.id, sheet_number: sheet.sheet_number,
      sheet_title: sheet.sheet_title, project_id: sheet.project_id,
    });
    setRecentList(getRecentSheets(sheet.id));
    // Iter EXP-2A — Last Location Resume.
    // Persist the current sheet so login flow can return the user
    // directly here next time. Keyed by user id so multi-tenant pods
    // don't cross-pollute.
    try {
      if (user?.id) {
        const label = sheet.sheet_number
          ? `${sheet.sheet_number}${sheet.sheet_title ? " · " + sheet.sheet_title : ""}`
          : (sheet.sheet_title || "your last sheet");
        window.localStorage.setItem(`fop:last-location:${user.id}`, JSON.stringify({
          path: `/sheet/${sheet.id}`,
          label,
          ts: Date.now(),
        }));
      }
    } catch { /* quota / private-mode noop */ }
  }, [sheet, user?.id]);

  // Refresh pins/objects/comments after ANY offline-queue drain
  // (manual sync button OR automatic background sync — flush() now
  // dispatches this centrally either way, see offlineQueueV2.js).
  useEffect(() => {
    const onSynced = () => { reload(); };
    window.addEventListener("forgedops:offline-synced", onSynced);
    return () => window.removeEventListener("forgedops:offline-synced", onSynced);
  }, [reload]);

  // The plan-set sheet list — used for prev/next, position counter, scrub
  // strip, and adjacent-sheet raster prefetch. Declared here BEFORE the
  // keyboard useEffect so the keyboard handler can navigate prev/next
  // without TDZ issues in its dependency array.
  const [projectSheets, setProjectSheets] = useState([]);
  useEffect(() => {
    if (!sheet?.project_id) return;
    let cancelled = false;
    api.get(`/projects/${sheet.project_id}/sheets`).then((r) => {
      if (cancelled) return;
      setProjectSheets(r.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sheet?.project_id]);

  // Keyboard shortcuts — extracted to useViewerShortcuts (Iter 36.5).
  useViewerShortcuts({
    sheetId, sheet, projectSheets, fieldMode,
    addMode, awaitingAnchorTap, measureMode, toolMode,
    measurePtsLength: measurePts.length,
    cropMode, markupMode, navigate,
    toggleFieldMode, toggleSidebar, setAddMode, setNewPinPos, setTemplate,
    setGpsPanelOpen, startMeasure, closeMeasure, undoMeasurePoint,
    openCrop, closeCrop, openMarkup, closeMarkup, setJumpOpen,
    setAwaitingAnchorTap, setAnchorTapPos,
  });

  // ----- Progressive raster pipeline (now delegated to TileManager) ------
  // TileManager owns the dpr=1 → dpr=2 → dpr=4 escalation logic. This
  // keeps SheetViewer free of raster-source concerns, and gives the future
  // multi-resolution tile pyramid a single drop-in point.
  const isRetina = useMemo(
    () => typeof window !== "undefined" && (window.devicePixelRatio || 1) >= 1.5,
    [],
  );
  const { rasterUrl, escalateForZoom } = useTileManager({
    sheetId: sheet?.id,
    isRetina,
    enabled: !!sheet,
  });

  // The canvas's onPanZoom callback emits {scale, fitScale, tx, ty}. Forward
  // to the tile manager for dpr escalation, capture the scale hint for the
  // header % indicator, and persist the viewport for TRACK 4 resume.
  const onPanZoomReport = useCallback(({ scale, fitScale, tx, ty }) => {
    setScaleHint(scale);
    escalateForZoom({ scale, fitScale });
    // TRACK 4 — persist the live viewport so reopening this sheet (today
    // or tomorrow) restores exactly where the operator was. Throttle via
    // a microtask coalesce so we don't write on every pan frame.
    if (sheet?.id && typeof scale === "number") {
      try {
        workspace.setSheetViewport(sheet.id, { scale, tx, ty });
      } catch { /* swallow — best-effort */ }
    }
    // TRACK 6 — measure the inter-frame interval as a pan-frame budget
    // proxy. Records into the local perf telemetry so the diagnostics
    // panel can answer "is pan smooth?" with real numbers.
    try {
      const now = performance.now();
      if (panZoomLastTickRef.current) {
        const dt = now - panZoomLastTickRef.current;
        // Anything > 250 ms means the callback wasn't firing at all
        // (idle gap) — ignore those as not-a-frame events.
        if (dt > 0 && dt < 250) {
          // Inline into the perf buffer
          // eslint-disable-next-line global-require
          import("@/lib/perfTelemetry").then((mod) => {
            mod.__perfInternals._samples.pan.push(dt);
            if (mod.__perfInternals._samples.pan.length > 200) {
              mod.__perfInternals._samples.pan.splice(
                0,
                mod.__perfInternals._samples.pan.length - 200,
              );
            }
          });
        }
      }
      panZoomLastTickRef.current = now;
    } catch { /* swallow */ }
  }, [escalateForZoom, sheet?.id]);

  // TRACK 4 — persist the last active tool so we can preselect it on
  // re-open of any sheet. Plain pan is the no-op default.
  useEffect(() => {
    try { workspace.setLastTool(activeTool || "pan"); } catch { /* swallow */ }
  }, [activeTool]);

  // Prefetch sibling sheets' rasters + thumbnails when the project is known,
  // so adjacent sheet switching feels instant. Idle-time only. ALWAYS at 1×
  // so the prefetch bytes-on-wire stay cheap (95-sheet airport sets × 2 MB
  // = 190MB total worst case at 2×; 1× is ~4× smaller).
  useEffect(() => {
    if (!sheet || !projectSheets.length) return;
    const idx = projectSheets.findIndex((s) => s.id === sheet.id);
    if (idx < 0) return;
    const neighbors = [projectSheets[idx - 1], projectSheets[idx + 1]].filter(Boolean);
    // Use requestIdleCallback when available so we never compete with the
    // user's pan/zoom interaction.
    const schedule = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
    const cancelSched = window.cancelIdleCallback || clearTimeout;
    const handle = schedule(() => {
      const token = localStorage.getItem("fop_bearer") || "";
      neighbors.forEach((s) => {
        const img = new Image();
        img.decoding = "async";
        // Prefetch at 1× only — keeps bytes cheap; the progressive swap will
        // upgrade to 2× after the sheet is actually opened and idle.
        img.src = `${process.env.REACT_APP_BACKEND_URL}/api/sheets/${s.id}/raster?token=${token}`;
      });
    }, { timeout: 1500 });
    return () => cancelSched(handle);
  }, [sheet, projectSheets]);

  // Swipe-left/right between sheets in the same project — only in Field Mode
  // (the gesture would collide with normal pan otherwise).
  const onSwipe = (dir) => {
    if (!fieldMode || !sheet || projectSheets.length < 2) return;
    const idx = projectSheets.findIndex((s) => s.id === sheet.id);
    if (idx < 0) return;
    const nextIdx = dir === "left" ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= projectSheets.length) return;
    navigate(`/sheet/${projectSheets[nextIdx].id}`);
  };

  // Current sheet position in the plan set (for top-bar "Sheet N of M"
  // counter + prev/next chevrons). Memoised so the header doesn't recompute
  // on every render.
  const sheetPosition = useMemo(() => {
    if (!sheet || !projectSheets.length) return { index: -1, total: 0, prev: null, next: null };
    const idx = projectSheets.findIndex((s) => s.id === sheet.id);
    return {
      index: idx,
      total: projectSheets.length,
      prev: idx > 0 ? projectSheets[idx - 1] : null,
      next: idx >= 0 && idx < projectSheets.length - 1 ? projectSheets[idx + 1] : null,
    };
  }, [sheet, projectSheets]);

  const goPrev = () => sheetPosition.prev && navigate(`/sheet/${sheetPosition.prev.id}`);
  const goNext = () => sheetPosition.next && navigate(`/sheet/${sheetPosition.next.id}`);

  // Pin drag end → persist new (x, y) via PATCH.
  const onPinDragEnd = async (pinId, nx, ny) => {
    try {
      await api.patch(`/pins/${pinId}`, { x: nx, y: ny });
      // Optimistic reload to confirm.
      reload();
    } catch (e) {
      console.error("Pin relocate failed", e);
      reload();
    }
  };

  const onCanvasClick = (nx, ny) => {
    // Track 4 — Asset placement takes priority if the operator armed the tool.
    if (assetMarkers.placementActive) {
      assetMarkers.placeAt(nx, ny);
      return;
    }
    // SC-4 — "I'm Here" manual position. Distinct from legacy `dropMode`
    // (lat/lng anchor) — this records to the new manual_positions
    // collection and explicitly tags `kind: 'manual'`. Never relabeled
    // as GPS downstream.
    if (imHereMode) {
      setImHereMode(false);
      setProjectPosition({
        nx, ny, kind: "manual", on_current_sheet: true,
        confidence: "low", accuracy_in_units: null,
        horizontal_units_label: "ft",
      });
      api.post(`/sheets/${sheet.id}/manual-position`, {
        nx, ny, gps_quality: null,
      }).then(() => {
        // Iter EXP-2A — Locate confidence moment.
        // Not "Manual position recorded." Show what was recorded and
        // what the operator can now do with it.
        toast.success("✓ You're on this sheet", {
          description: `Position locked on ${sheet.sheet_number || "current sheet"}. Tap Measure or Markup to keep working from here.`,
          duration: 5500,
        });
      }).catch((e) => {
        toast.error("Couldn't save your position", {
          description: e?.response?.data?.detail || e.message || "Try again or use Drop Position.",
          duration: 6000,
        });
      });
      return;
    }
    // Iter 34 — Drop Position mode: next canvas tap is a manual marker.
    // No GPS calibration required. Works on any sheet, always.
    if (dropMode) {
      setManualPosition({ nx, ny });
      setDropMode(false);
      return;
    }
    // If awaiting an anchor tap for calibration → capture & open the form.
    if (awaitingAnchorTap) {
      setAnchorTapPos({ nx, ny });
      setAwaitingAnchorTap(false);
      setGpsPanelOpen(true);
      return;
    }
    if (!addMode) return;
    if (template) {
      // 2-second pin entry: drop with the template values pre-filled.
      submitPin({
        x: nx, y: ny,
        title: template.title,
        category: template.category,
        priority: template.priority,
      });
      setTemplate(null);
    } else {
      setNewPinPos({ x: nx, y: ny });
    }
  };

  const onLongPress = (nx, ny) => {
    // Long-press anywhere on the drawing → manual "I'm here" marker.
    setManualPosition({ nx, ny });
    setGpsPanelOpen(true);
  };

  const submitPin = async (payload, _retryOpId) => {
    const fullPayload = { page: 1, ...payload };
    // FP-034 (2026-09-09 audit remediation) — stable id shared by
    // BOTH the online and offline-queued paths below, AND reused
    // across the manual "retry" button (rather than regenerated per
    // attempt), so no retry path can ever create a duplicate pin
    // server-side.
    const clientOpId = _retryOpId || (
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID() : `cop_${Date.now()}_${Math.floor(Math.random() * 1e9)}`
    );
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      // FP-034 — migrated off the abandoned v1 IndexedDB pin queue
      // (offlineQueue.js), which had none of the identity-scoping,
      // durable-persistence, dead-letter-visibility or idempotency
      // protections added to v2 for markups/punch. This was a live,
      // untested path left behind a general "offline fixed" claim.
      const localId = offlineEnqueue("pin.create", {
        sheetId, body: { ...fullPayload, client_op_id: clientOpId },
      });
      setPins((prev) => [...prev, {
        id: localId, sheet_id: sheetId, ...fullPayload,
        status: "open", workflow_ids: [], _pending_sync: true,
      }]);
      setAddMode(false); setNewPinPos(null);
      setActivePin(localId);
      return;
    }
    try {
      const { data: pin } = await api.post("/pins",
        { sheet_id: sheetId, ...fullPayload, client_op_id: clientOpId });
      setAddMode(false); setNewPinPos(null);
      await reload();
      setActivePin(pin.id);
    } catch (e) {
      // Iter 179 — surface backend rejection instead of a silent throw.
      confirm.saveFailed("Couldn't drop pin", {
        error: e,
        retry: () => submitPin(payload, clientOpId),
      });
      throw e; // preserve upstream flow (the modal may want to keep its state)
    }
  };

  if (loadError) {
    const notFound = loadError?.response?.status === 404;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--fop-bg)] p-6" data-testid="sheet-viewer-error-state">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-[var(--fop-cta-bg)]/10 border border-[var(--fop-cta-bg)]/30 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7 text-[var(--fop-cta-bg)]" />
          </div>
          <h1 className="text-xl font-bold text-[var(--fop-text)]" data-testid="sheet-viewer-error-title">
            {notFound ? "Sheet not found" : "Couldn't load this sheet"}
          </h1>
          <p className="text-sm text-[var(--fop-text-3)]">
            {notFound
              ? "This sheet doesn't exist, was removed, or you don't have access to it."
              : "Something went wrong while loading this sheet. Check your connection and try again."}
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              data-testid="sheet-viewer-error-retry-btn"
              onClick={() => reload().catch((e) => setLoadError(e))}
              className="btn btn-ghost px-4 py-2 text-sm"
            >
              Try again
            </button>
            <button
              type="button"
              data-testid="sheet-viewer-error-back-btn"
              onClick={() => (routeProjectId ? navigate(`/projects/${routeProjectId}`) : navigate("/portfolio"))}
              className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> Back to project
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!sheet) return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]" data-testid="sheet-viewer-loading">Loading sheet…</div>;
  // GH-05 — `sheet` can briefly still hold the PREVIOUS sheet's data
  // while `reload()` for the newly-routed `sheetId` is in flight (this
  // is normal and expected — React hasn't re-rendered with the new
  // sheet yet). Rendering the stale sheet's drawing/heading/tools during
  // this window is exactly the defect: a user could place a pin or
  // markup against coordinates meant for the OLD drawing while it's
  // saved under the NEW sheetId. Show a lightweight transition state
  // instead — nothing interactive renders, so placement is inherently
  // disabled for the duration of the identity mismatch, not just the
  // data race underneath it.
  if (sheet.id !== sheetId) {
    return (
      <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]" data-testid="sheet-viewer-switching">
        Switching sheets…
      </div>
    );
  }

  const activePinObj = pins.find((p) => p.id === activePin);

  return (
    <div
      className="fixed inset-0 flex flex-col"
      data-testid="sheet-viewer"
      style={{
        // PILLAR 4B — token-driven viewer root so Daylight gets paper
        // and Field Black gets near-black via the same path. Removes
        // the visible bleed the operator called out.
        background: "var(--fop-bg)",
        // Iter 144 — iPad safe-area insets. Without these, iPadOS status
        // bar (time/date/battery) bleeds into the viewer chrome. The
        // chrome (top bar / status bar) lives inside this fixed root, so
        // padding the root pushes everything inside the safe area while
        // the drawing-canvas grid still occupies the rest.
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
        // Iter 146 — block Safari's double-tap-to-zoom of the WHOLE PAGE.
        // The canvas implements its own pinch internally; without this,
        // a fast two-tap measurement on iPad accidentally triggered a
        // page zoom-to-element, which was the operator's "random zoom"
        // complaint. `manipulation` allows panning + pinching but blocks
        // double-tap zoom of the SPA chrome.
        touchAction: "manipulation",
      }}
    >

      {/* Top bar — drawing-room orientation. Hidden in Field Mode. */}
      {!fieldMode && (
        <div className="h-14 flex items-center gap-3 px-3 border-b border-[var(--fop-border)] bg-[var(--fop-surface)]/95 backdrop-blur z-20" data-testid="sheet-viewer-topbar">
          <button
            onClick={() => navigate(sheet.project_id ? `/project/${sheet.project_id}/plans` : "/portfolio")}
            className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] transition-colors shrink-0"
            data-testid="back-to-project"
            aria-label="Back to plan wall"
            title="Back to plan wall"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Iter 178 — Identity block: SINGLE ROW, hero-first.
              Sheet number is the largest thing on the screen after the
              drawing itself; the project + discipline collapse into
              small mono meta text; extra chips only appear when they
              carry live information the operator can act on. */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <span className="font-mono text-[16px] font-bold text-[var(--fop-text)] tracking-tight shrink-0" data-testid="header-sheet-number">
              {sheet.sheet_number}
            </span>
            <span className="hidden md:inline text-[13px] text-[var(--fop-text-2)] truncate max-w-[280px]" data-testid="header-sheet-title">
              {sheet.sheet_title || <em className="text-[var(--fop-text-3)] italic">untitled</em>}
            </span>
            <span className="hidden lg:inline text-[var(--fop-text-3)]/40 text-[10px]">·</span>
            <div className="hidden lg:flex items-center gap-1.5 min-w-0 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
              {project && (
                <button
                  onClick={() => navigate(`/project/${project.id}/plans`)}
                  className="hover:text-[var(--fop-primary)] transition-colors truncate max-w-[220px]"
                  data-testid="header-project-name"
                  title={project.name}
                >
                  {project.name}
                </button>
              )}
              {project && <span className="text-[var(--fop-text-3)]/40">›</span>}
              <span>{DISCIPLINE_LABEL[sheet.discipline] || sheet.discipline}</span>
              {project?.internal && <InternalBadge size="xs" />}
            </div>
            <Link
              to={`/sheet/${sheet.id}/compare`}
              className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-[11px] font-mono uppercase tracking-wider transition-colors shrink-0 ${
                revStatus && revStatus.is_latest === false
                  ? "border-amber-500/60 text-amber-400 hover:border-amber-400 hover:text-amber-300"
                  : "border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-primary)]"
              }`}
              data-testid="open-revision-compare"
              title={
                revStatus
                  ? revStatus.is_latest
                    ? `Latest revision of ${sheet.sheet_number} (${revStatus.total_revisions} total). Tap to compare.`
                    : `${revStatus.revisions_behind} revision${revStatus.revisions_behind === 1 ? "" : "s"} behind latest (${revStatus.latest_revision}). Tap to compare.`
                  : "Compare with previous revisions"
              }
            >
              REV {sheet.revision}
              {revStatus && revStatus.total_revisions > 1 && (
                revStatus.is_latest ? (
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] tracking-wider"
                    data-testid="rev-latest-badge"
                  >
                    LATEST
                  </span>
                ) : (
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 tracking-wider"
                    data-testid="rev-behind-badge"
                  >
                    {revStatus.revisions_behind} BEHIND
                  </span>
                )
              )}
            </Link>
            {/* GH-10 — explicit, visible "unknown" state when the
                revision_status fetch failed, instead of silently
                falling back to a plain REV pill that could be mistaken
                for "confirmed latest". Click retries without reloading
                the whole sheet. */}
            {revStatusError && (
              <button
                onClick={() => setRevStatusRetryNonce((n) => n + 1)}
                className="hidden lg:inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[var(--fop-border-2)] text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] hover:border-[var(--fop-primary)] transition-colors shrink-0"
                data-testid="rev-status-unknown-retry"
                title="Revision status could not be checked — tap to retry"
              >
                REV STATUS UNKNOWN · RETRY
              </button>
            )}
            {/* Iter 178 — Suppress the "Calibrated · Good" pill when the
                Trust Bar is already surfacing readiness. The pill remains
                available for future field UIs; for the header it duplicates
                information (single source of truth: TrustBar). */}
            {sheet.needs_review && (
              <div className="hidden lg:inline-flex">
                <NeedsReviewBadge
                  sheet={sheet}
                  canEdit={canEditMetadata}
                  onSaved={(updated) => setSheet({ ...sheet, ...updated })}
                  size="sm"
                />
              </div>
            )}
            {ackSummary?.mine?.total > 0 && (
              <div className="hidden xl:inline-flex">
                <AckPill
                  counts={ackSummary.mine}
                  scope="mine"
                  testid="sheet-ack-pill-mine"
                  onClick={() => focusFirstImpact()}
                />
              </div>
            )}
          </div>

          {/* Position + prev/next sheet navigation — Iter 146: counter text
              hidden on iPad widths; prev/next chevrons remain critical. */}
          {sheetPosition.total > 1 && sheetPosition.index >= 0 && (
            <div className="flex items-center gap-1 ml-2 pl-3 border-l border-[var(--fop-border-2)]" data-testid="sheet-position-nav">
              <button
                onClick={goPrev}
                disabled={!sheetPosition.prev}
                className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                data-testid="sheet-prev"
                title={sheetPosition.prev ? `Previous (${sheetPosition.prev.sheet_number})` : "No previous sheet"}
                aria-label="Previous sheet"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="hidden lg:block text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] px-1.5 min-w-[80px] text-center" data-testid="sheet-position-counter">
                Sheet {sheetPosition.index + 1} of {sheetPosition.total}
              </div>
              <button
                onClick={goNext}
                disabled={!sheetPosition.next}
                className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                data-testid="sheet-next"
                title={sheetPosition.next ? `Next (${sheetPosition.next.sheet_number})` : "No next sheet"}
                aria-label="Next sheet"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          <div className="ml-auto flex items-center gap-1" data-testid="sheet-viewer-toolbar">
            {/* Iter 63 P0-1 — always-visible search (Cmd+K equivalent for iPad). */}
            <SearchTrigger compact />

            {/* Zoom group — kept compact (constantly used) */}
            <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] mr-1 ml-1" data-testid="zoom-percent">
              {Math.round(scaleHint * 100)}%
            </span>
            <button onClick={() => { dispatchCanvasCmd(sheetId, "zoomOut"); trackViewerEvent("viewer.zoom_out", { sheet_id: sheetId }); }}
                     className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] transition-colors"
                     data-testid="zoom-out" title="Zoom out" aria-label="Zoom out">
              <ZoomOut size={17} />
            </button>
            <button onClick={() => { dispatchCanvasCmd(sheetId, "zoomIn"); trackViewerEvent("viewer.zoom_in", { sheet_id: sheetId }); }}
                     className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] transition-colors"
                     data-testid="zoom-in" title="Zoom in" aria-label="Zoom in">
              <ZoomIn size={17} />
            </button>

            <div className="divider-v mx-1" />

            {/* Primary tools — kept on the bar (Tier A in the use-frequency audit). */}
            <button
              onClick={() => { setJumpOpen(true); trackViewerEvent("viewer.jump_open", { sheet_id: sheetId }); }}
              className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] transition-colors"
              data-testid="sheet-jump-trigger"
              title="Jump to sheet (J or #)"
              aria-label="Jump to sheet"
            >
              <Hash size={17} />
            </button>
            {/* Iter 178.4 — Ruler + Field-mode icons removed from the top
                bar. Both were duplicating the left-rail tool selectors
                (Measure and Field on the rail are canonical). One tool,
                one place. */}

            {/* PILLAR 2 — Measurement history toggle. Field crews
                repeatedly asked for the last-20 list with rename / delete.
                Lives next to the measure button so the muscle-memory path
                is one tap from the same tool. */}
            <TouchTooltip content="Measurement history" delay={400} offsetY={6}>
              <button
                onClick={() => setHistoryOpen((v) => !v)}
                className={`h-10 w-10 flex items-center justify-center rounded-md transition-colors ${historyOpen ? "text-[var(--fop-primary)] bg-[var(--fop-primary-soft)]" : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)]"}`}
                data-testid="measurement-history-toggle"
                title="Measurement history"
                aria-label="Measurement history"
              >
                <HistoryIcon size={17} />
              </button>
            </TouchTooltip>

            {/* Wave 6 (2026-09-11) — star/favorite this sheet for the
                My Sheets quick-jump list. */}
            <TouchTooltip content={favorited ? "Remove from My Sheets" : "Add to My Sheets"} delay={400} offsetY={6}>
              <button
                onClick={() => { toggleFavorite(); trackViewerEvent("viewer.favorite_toggle", { sheet_id: sheetId, favorited: !favorited }); }}
                disabled={favBusy}
                className={`h-10 w-10 flex items-center justify-center rounded-md transition-colors ${favorited ? "text-[var(--fop-warning)]" : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)]"}`}
                data-testid="sheet-favorite-toggle"
                title={favorited ? "Remove from My Sheets" : "Add to My Sheets"}
                aria-label={favorited ? "Remove from My Sheets" : "Add to My Sheets"}
                aria-pressed={favorited}
              >
                <Star size={17} fill={favorited ? "currentColor" : "none"} />
              </button>
            </TouchTooltip>

            {/* Iter 63.1 — "More" overflow popover. Holds secondary tools
                (Tier B/C/D in the use-frequency audit) so the bar stays
                under 8 visible buttons on iPad. */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="h-10 w-10 flex items-center justify-center rounded-md text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] transition-colors"
                  data-testid="viewer-more-trigger"
                  title="More tools (tap-and-hold for label)"
                  aria-label="More tools"
                  onClick={() => trackViewerEvent("viewer.more_open", { sheet_id: sheetId })}
                >
                  <MoreHorizontal size={17} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-56 p-1 bg-[var(--fop-surface)] border-[var(--fop-border)] text-[var(--fop-text)]"
                data-testid="viewer-more-menu"
              >
                <MoreItem
                  onClick={() => { dispatchCanvasCmd(sheetId, "fit"); trackViewerEvent("viewer.zoom_fit", { sheet_id: sheetId }); }}
                  icon={Maximize2} label="Fit to screen"
                  testid="zoom-fit"
                />
                <MoreItem
                  active={cropMode}
                  onClick={() => { cropMode ? closeCrop() : openCrop(); trackViewerEvent("viewer.measure_area_toggle", { sheet_id: sheetId, kind: cropMode ? "off" : "on" }); }}
                  icon={Crop} label="Measure area" hint="C"
                  testid="crop-toggle"
                />
                <MoreItem
                  active={markupMode}
                  onClick={() => { markupMode ? closeMarkup() : openMarkup(); trackViewerEvent("viewer.markup_toggle", { sheet_id: sheetId, kind: markupMode ? "off" : "on" }); }}
                  icon={Pencil} label="Markup / redline" hint="N"
                  testid="markup-toggle"
                />
                <MoreItem
                  active={gpsPanelOpen || gpsEnabled}
                  onClick={() => { setGpsPanelOpen((v) => !v); trackViewerEvent("viewer.gps_toggle", { sheet_id: sheetId }); }}
                  icon={Navigation} label="My GPS position" hint="G"
                  testid="gps-panel-toggle"
                />
                <MoreItem
                  active={utilityIntelVisible}
                  onClick={() => { toggleUtilityIntel(); trackViewerEvent("viewer.utilities_toggle", { sheet_id: sheetId, kind: utilityIntelVisible ? "off" : "on" }); }}
                  icon={AlertTriangle} label="Utilities layer" hint="U"
                  testid="utility-intel-toggle"
                />
                {canAuthorPipes(user) && (
                  <MoreItem
                    active={addPipeMode}
                    onClick={() => {
                      setPipeError(null);
                      setPipeFromStruct(null);
                      // Iter FIELD-UX-2 — funnel through arm/disarm so we
                      // can never coexist with measure/markup/imHere/etc.
                      if (addPipeMode) { setAddPipeMode(false); }
                      else { armAddPipe(); }
                      trackViewerEvent("viewer.add_pipe_toggle", { sheet_id: sheetId, kind: addPipeMode ? "off" : "on" });
                    }}
                    icon={GitBranch} label="Add pipe run"
                    testid="add-pipe-toggle"
                  />
                )}
                {/* SC-3 — Open the Sheet → Project Calibration Studio.
                    Distinct from per-sheet Set Scale (operational only). */}
                <MoreItem
                  onClick={() => { window.location.assign(`/sheet/${sheetId}/calibrate`); }}
                  icon={Compass} label="Project calibration"
                  testid="open-calibration-studio"
                />
                <MoreItem
                  onClick={() => setEarthworkOpen(true)}
                  icon={Mountain} label="Earthwork (cut/fill)"
                  testid="open-earthwork-estimator"
                />
                <div className="my-1 h-px bg-[var(--fop-border)]" />
                <MoreItem
                  onClick={() => setShareOpen(true)}
                  icon={Share2} label="Share sheet"
                  testid="viewer-share-more"
                />
                <MoreItem
                  onClick={async () => {
                    try {
                      if (sheet?.project_id && allSheets.length === 0) {
                        const r = await api.get(`/projects/${sheet.project_id}/sheets`);
                        setAllSheets(Array.isArray(r.data) ? r.data : []);
                      }
                    } finally {
                      setExportOpen(true);
                    }
                  }}
                  icon={FileDown} label="Export plan (PDF)"
                  testid="viewer-export-more"
                />
                <MoreItem
                  onClick={() => { toggleSidebar(); trackViewerEvent("viewer.sidebar_toggle", { sheet_id: sheetId }); }}
                  active={sidebarOpen}
                  icon={PanelRight} label={sidebarOpen ? "Hide inspector" : "Show inspector"}
                  testid="toggle-sidebar-more"
                />
              </PopoverContent>
            </Popover>

            <div className="divider-v mx-1" />

            {/* Iter 178 — Share / Export / Sidebar-toggle moved into the More
                menu on all widths. They were rarely-used but visually loud
                — pulling them out shrinks the top-bar cluster from 14 to 8
                controls and lets Add Pin actually breathe. Keep the field-
                mode toggle inline: it flips the whole chrome. */}
            {/* Iter 178.4 — Field Mode toggle removed from top bar (canonical
                trigger is the FIELD button on the left rail). */}

            {/* Add Pin — the single canonical pin-creation entry point.
                Iter 63.1: toggling Quick Pin clears addMode (one path, one
                state).
                Iter 146 — hidden during active measurement so the topbar
                does not invite an off-task action in the middle of a
                measure session. */}
            {measureMode === "off" && (
              <button
                onClick={() => {
                  // Iter FIELD-UX-2 — exclusive arm/disarm.
                  if (addMode) { setAddMode(false); setNewPinPos(null); setTemplate(null); }
                  else { armAddPin(); setNewPinPos(null); setTemplate(null); }
                  trackViewerEvent("viewer.add_pin_toggle", { sheet_id: sheetId, kind: addMode ? "off" : "on" });
                }}
                className={`h-10 inline-flex items-center gap-1.5 px-4 rounded-md text-[13px] font-semibold tracking-tight transition-all ml-1 shadow-sm ${addMode ? "bg-[var(--fop-cta-bg)] text-white hover:bg-[var(--fop-cta-bg-hover)]" : "bg-[var(--fop-cta-bg)] text-white hover:bg-[var(--fop-cta-bg-hover)] hover:shadow-md"}`}
                data-testid="add-pin-toggle"
              >
                {addMode ? <X size={15} strokeWidth={2.5} /> : <Plus size={15} strokeWidth={2.5} />}
                {addMode ? "Cancel" : "Add Pin"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Iter 144 — field-mode brand emblem removed. It clashed with the
          EXIT FIELD pill at top-left and added pure decorative noise.
          Drawings remain the hero. */}

      {/* Iter 50 — Revision Impact Stations banner. Only renders when the
          sheet has at least one alignment AND a structure-derived station
          range is resolvable. Tap → jumps to the alignment object. */}
      {!fieldMode && <RevisionImpactStationsBanner sheetId={sheet?.id}
                                                      reviewId={activeReview?.id} />}

      {/* LAST-CHANCE RESCUE — Add Pipe hint is now a small floating pill,
          not a full-width orange banner. Drawing remains the hero; the
          operator can still see the next action without giving up plan
          real estate. */}

      {/* Scrub-strip handshake — same binder thumb-index the user just used
          to enter this sheet. Persistent soft glow on the active cell + a
          600ms pulse every time the sheet changes (handshake feedback) OR
          a pin is activated (severity-tinted pulse, momentary only — not
          persistent state).
          Hidden in Field Mode so the drawing fills the viewport. */}
      {!fieldMode && projectSheets.length >= 8 && (
        <DensityScrubStrip
          sheets={projectSheets}
          activeId={sheet?.id}
          pulseTick={scrubPulseTick}
          pulseSeverity={scrubPulseSeverity}
          rightOffsetPx={sidebarOpen ? 312 : 6}
          data-testid="viewer-scrub-strip"
        />
      )}

      {/* PILLAR 1 — Sheet Trust Bar. Unified, plain-English verdict.
          Lives just above the canvas so the operator can answer
          "can I build from this sheet?" in one glance — without the
          chrome screaming. Hidden in Field Mode (the drawing owns
          the entire viewport there). */}
      {!fieldMode && sheet?.id && (
        <div
          className="h-11 px-3 border-b border-[var(--fop-border)] bg-[var(--fop-surface)]/95 backdrop-blur flex items-center gap-2"
          data-testid="viewer-trust-strip"
        >
          <TrustBar
            sheetId={sheet.id}
            scope="sheet"
            onAction={(action) => {
              if (!action) return;
              if (action.type === "open_sheet" && action.target) {
                navigate(`/sheet/${action.target}`);
              } else if (action.type === "calibrate_sheet" && action.target) {
                navigate(`/sheet/${action.target}?mode=calibrate`);
              } else if (action.type === "open_survey_control" && action.target) {
                navigate(`/projects/${action.target}/survey-control`);
              } else if (action.type === "place_alignment" && action.target) {
                navigate(`/sheet/${action.target}?mode=alignment`);
              } else if (action.type === "open_revision_review" && action.target) {
                navigate(`/sheet/${action.target}/compare`);
              } else if (action.type === "open_war_room" && action.target) {
                // Iter 178.6 — Wire the Trust Bar's "Open War Room" CTA.
                // Route is defined in App.js as /projects/:projectId/war-room.
                // Without this branch the button was a silent no-op —
                // real defect surfaced by iteration_178 functional sweep.
                navigate(`/projects/${action.target}/war-room`);
              } else {
                // Loud fail-visible fallback so future missing dispatchers
                // don't reach production as silent no-ops.
                // eslint-disable-next-line no-console
                console.warn(`[TrustBar] Unhandled action.type='${action.type}' target='${action.target}' — no route dispatcher in SheetViewer.onAction. Add a branch to fix.`);
              }
            }}
          />
          <div className="flex-1" />
          {/* Iter 178.3 — "Where am I?" moved off the floating overlay and
              inline into the trust strip. The pill was fighting the
              drawing (fixed top-right, above the sidebar) and duplicating
              readiness signal. Living inside the trust strip lets it read
              as a "check my location" affordance next to the sheet-trust
              affordance — same row, same visual language. */}
          <button
            type="button"
            onClick={() => setProjectLocateOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[var(--fop-surface-2)]/60 hover:bg-[var(--fop-surface-3)] border border-[var(--fop-border-2)] hover:border-[var(--fop-primary)]/60 text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] text-[12px] font-mono uppercase tracking-wider transition-colors shrink-0"
            data-testid="open-project-locate"
            title="Where am I on this project?"
          >
            <Compass size={13} strokeWidth={2.5} />
            Where am I?
          </button>
        </div>
      )}

      {/* Canvas + optional sidebar (Iter 73 — left tool rail prepended) */}
      <div className={`flex-1 min-h-0 grid relative ${
        fieldMode
          ? "grid-cols-1"
          : sidebarOpen ? "grid-cols-[76px_1fr] lg:grid-cols-[76px_1fr_300px]" : "grid-cols-[76px_1fr]"
      }`}>
        {/* Iter EXP-5 — Quiet active-tool indicator.
            Demoted from EXP-1's full-width orange strip (the operator
            named this exact element as making the drawing harder to
            use). Now a minimal floating pill in the top-right corner.
            Cancel remains a tap target but does not dominate the sheet.
            The drawing is the hero again. */}
        {activeTool !== "pan" && (
          <div
            className="absolute right-3 z-[60] inline-flex items-center gap-2 rounded-md bg-[rgba(15,23,42,0.92)] backdrop-blur-sm text-white shadow-lg border border-[var(--fop-primary)]/60 px-2.5 py-1 max-w-[min(70vw,420px)]"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
            data-testid="active-tool-indicator"
            data-active-tool={activeTool}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--fop-primary)] animate-pulse shrink-0" aria-hidden="true" />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-[12px] font-semibold tracking-wide uppercase truncate" data-testid="active-tool-label">
                {activeToolMeta.label}
              </span>
              {/* PILLAR 2 — next-expected-action so the operator never
                  wonders what the next tap does. Truncates on iPad. */}
              {activeToolMeta.hint && (
                <span
                  className="text-[10px] text-white/80 font-normal truncate"
                  data-testid="active-tool-hint"
                >
                  {activeToolMeta.hint}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={cancelActiveTool}
              className="ml-1 rounded-sm text-white/70 hover:text-white hover:bg-white/10 w-6 h-6 flex items-center justify-center transition-colors shrink-0"
              data-testid="active-tool-cancel"
              aria-label="Cancel current tool"
              title="Cancel current tool"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {/* LAST-CHANCE RESCUE — Add Pipe instruction pill (quiet, drawing-first). */}
        {addPipeMode && (
          <div
            className="absolute left-1/2 -translate-x-1/2 z-[60] inline-flex items-center gap-2 rounded-md bg-[rgba(15,23,42,0.92)] backdrop-blur-sm text-white shadow-lg border border-[var(--fop-primary)]/60 px-2.5 py-1"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
            data-testid="add-pipe-banner"
            data-stage={pipeFromStruct ? "second" : "first"}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <GitBranch size={12} className="text-[var(--fop-primary)]" />
            <span className="text-[12px] font-medium leading-none">
              {pipeBusy
                ? "Creating pipe…"
                : pipeFromStruct
                  ? <>From <b>{pipeFromStruct.structure_label}</b> → tap destination</>
                  : "Tap the FROM structure"}
            </span>
            <button
              type="button"
              onClick={() => { setAddPipeMode(false); setPipeFromStruct(null); setPipeError(null); }}
              className="ml-1 rounded-sm text-white/70 hover:text-white hover:bg-white/10 w-6 h-6 flex items-center justify-center transition-colors"
              data-testid="add-pipe-cancel"
              aria-label="Cancel add pipe"
            >
              <X size={14} />
            </button>
            {pipeError && (
              <span className="ml-2 text-red-300 text-[11px]" data-testid="add-pipe-error">{pipeError}</span>
            )}
          </div>
        )}
        {!fieldMode && (
          <SheetToolRail
            mode={
              measureMode !== "off" ? "measure"
              : markupMode ? "markup"
              : addMode ? "pin"
              : "select"
            }
            onSelect={() => { closeAllToolModes(); }}
            onPan={() => { closeAllToolModes(); }}
            onAddPin={() => { if (addMode) { setAddMode(false); } else { armAddPin(); } setNewPinPos(null); setTemplate(null); }}
            onMarkup={openMarkup}
            onMeasure={() => { if (measureMode === "off") startMeasureExclusive("distance"); else closeMeasure(); }}
            onAsset={() => { setAssetPaletteOpen((v) => !v); }}
            assetOpen={assetPaletteOpen}
            onLayers={() => { if (!sidebarOpen) toggleSidebar(); window.dispatchEvent(new CustomEvent("forgedops:inspector-tab", { detail: { tab: "layers" } })); }}
            onObjects={() => { if (!sidebarOpen) toggleSidebar(); window.dispatchEvent(new CustomEvent("forgedops:inspector-tab", { detail: { tab: "objects" } })); }}
            onRevisions={() => { if (!sidebarOpen) toggleSidebar(); window.dispatchEvent(new CustomEvent("forgedops:inspector-tab", { detail: { tab: "revisions" } })); }}
            onDocuments={() => { if (sheet?.project_id) navigate(`/project/${sheet.project_id}/documents`); }}
            fieldMode={fieldMode}
            onToggleFieldMode={toggleFieldMode}
          />
        )}
        <FirstTimeViewerHint hidden={fieldMode} />
        {/* Wave 7 (2026-09-11) — superseded-drawing watermark. Field-first
            safety requirement: a small header badge (REV X BEHIND, added
            earlier) is easy to miss while a crew is focused on the drawing
            itself. This reuses the SAME revStatus data (no second
            revision-truth subsystem) to stamp an unmissable, non-blocking
            overlay directly across the canvas whenever the sheet being
            viewed is NOT the latest revision — in field mode too, since
            that is exactly where building off an outdated drawing is most
            dangerous. pointer-events-none so it never intercepts drawing,
            measuring, or panning. */}
        {revStatus && revStatus.is_latest === false && (
          <div
            className="absolute inset-0 z-[55] flex items-center justify-center pointer-events-none select-none overflow-hidden"
            data-testid="superseded-watermark"
            data-revisions-behind={revStatus.revisions_behind}
          >
            <span
              className="font-black uppercase tracking-[0.15em] whitespace-nowrap text-amber-500"
              style={{
                fontSize: "min(9vw, 96px)",
                opacity: 0.16,
                transform: "rotate(-24deg)",
                textShadow: "0 0 1px rgba(0,0,0,0.4)",
              }}
            >
              SUPERSEDED
            </span>
          </div>
        )}
        {revStatus && revStatus.is_latest === false && (
          <div
            className="absolute left-1/2 -translate-x-1/2 z-[56] inline-flex items-center gap-2 rounded-md bg-amber-500/95 text-black shadow-lg px-3 py-1.5 pointer-events-none"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
            data-testid="superseded-banner"
          >
            <AlertTriangle size={14} strokeWidth={2.5} />
            <span className="text-[12px] font-bold uppercase tracking-wide">
              Superseded — {revStatus.revisions_behind} rev{revStatus.revisions_behind === 1 ? "" : "s"} behind (latest: REV {revStatus.latest_revision})
            </span>
          </div>
        )}
        <SheetCanvas
          sheetId={sheet.id}
          sheetRevision={sheet.revision}
          rasterUrl={rasterUrl}
          pins={[...(pins || []), ...overlayMarkers.pins, ...assetMarkers.pins]}
          addMode={(addMode && !template ? true : addMode) || awaitingAnchorTap || dropMode || imHereMode}
          measureMode={measureMode !== "off"}
          onMeasureClick={onMeasureClickSnapped}
          onMeasureHover={onMeasureHover}
          onMeasureScreenMove={(x, y, isTouch) => setMeasureScreenPos({ x, y, isTouch: !!isTouch })}
          onMeasurePrecisionHold={(active) => setPrecisionHold(!!active)}
          pinDragEnabled={!addMode && measureMode === "off" && !cropMode && !markupMode}
          onPinDragEnd={onPinDragEnd}
          onPinTap={(pinId) => setActivePin((cur) => (cur === pinId ? null : pinId))}
          cropMode={cropMode}
          onCropDrag={(r) => setCropRect(r)}
          onCropEnd={() => {}}
          markupMode={markupMode}
          markupTool={markupState.tool}
          markupInProgress={!!markupInteraction.inProgress}
          onMarkupClick={markupInteraction.onClick}
          onMarkupHover={markupInteraction.onHover}
          onMarkupDragStart={markupInteraction.onFreehandStart}
          onMarkupDragMove={markupInteraction.onFreehandMove}
          onMarkupDragEnd={markupInteraction.onFreehandEnd}
          onImgDims={setViewerImgDims}
          onPanZoom={onPanZoomReport}
          onCanvasClick={onCanvasClick}
          placementActive={assetMarkers.placementActive || imHereMode}
          onLongPress={onLongPress}
          onSwipe={onSwipe}
          focusedStructureId={focusedStructureId}
          onStructurePick={onStructurePick}
          className="w-full h-full"
          testid="sheet-canvas"
          overlay={({ scale, imgDims }) => (
            <>
              {/* Iter 37 — Utility Intelligence layer. Rendered FIRST so
                  it sits BEHIND the LayerOverlay structure markers and
                  saved measurements. Restrained tint — drawing dominates. */}
              <UtilityIntelLayer
                sheetId={sheet?.id}
                visible={utilityIntelVisible}
                focusedCrossingId={activeCrossing?.id}
                onPick={(c) => {
                  // Iter 45 — line crossings come from a different code
                  // path with segment-anchored data. Normalize to the V1
                  // inspector shape (a/b/midpoint/clearance_ft/label_pair)
                  // so the ConflictInspector component can render either.
                  if (c && c.kind === "line_crossing") {
                    setActiveCrossing({
                      id: c.id,
                      kind: "line_crossing",
                      severity: c.severity,
                      label_pair: c.label_pair,
                      clearance_ft: c.estimated_clearance_ft,
                      midpoint: c.intersection,
                      a: {
                        layer_type: c.layer_a,
                        structure_label: `${c.segment_a.start_label || "?"} → ${c.segment_a.end_label || "?"}`,
                        elevation: c.elevation_a,
                      },
                      b: {
                        layer_type: c.layer_b,
                        structure_label: `${c.segment_b.start_label || "?"} → ${c.segment_b.end_label || "?"}`,
                        elevation: c.elevation_b,
                      },
                    });
                  } else {
                    setActiveCrossing(c);
                  }
                }}
              />
              <GpsLocationLayer
                transform={gpsTransform}
                position={gpsEnabled ? livePosition : null}
                manual={manualPosition}
                scale={scale}
              />
              {/* Iter 73 Phase 7 — Manual utility marker overlay.
                  Reads project_objects with type=utility for this sheet. */}
              <UtilityOverlay
                projectId={sheet?.project_id}
                sheetId={sheet?.id}
                visible={layerVisibility.utilities !== false}
                scale={scale}
                imgDims={imgDims}
              />
              {/* Iter 51 — persistent pipe runs. Tap → pipe inspector. */}
              <PipeLayer
                sheetId={sheet?.id}
                imgDims={imgDims}
                viewScale={scale}
                reloadKey={pipeReloadKey}
                addPipeMode={addPipeMode}
              />
              {/* Iter 33 — persistent saved measurements. Rendered ABOVE
                  pins/markup but UNDER the live in-progress measure
                  overlay so a new measurement reads as the "loud" one. */}
              <MeasurementsLayer
                sheetId={sheet?.id}
                imgDims={imgDims}
                viewScale={scale}
                reloadKey={measurementsReloadKey}
              />
              {/* SC-4 — project-locate position marker. Renders the live
                  GPS-derived (nx, ny) point on the current sheet with an
                  accuracy ring sized in the project's horizontal units. */}
              {projectPosition && projectPosition.on_current_sheet && imgDims && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: projectPosition.nx * imgDims.w,
                    top: projectPosition.ny * imgDims.h,
                    transform: "translate(-50%, -50%)",
                  }}
                  data-testid="project-position-marker"
                  data-kind={projectPosition.kind}
                >
                  {projectPosition.kind === "manual" && (
                    <div className="absolute left-3 -top-3 whitespace-nowrap px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[var(--fop-cta-bg)] text-white"
                          data-testid="manual-position-badge">
                      MANUAL POSITION
                    </div>
                  )}
                  <div className="w-3 h-3 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(34,197,94,0.85)] bg-[rgb(34,197,94)]" />
                </div>
              )}
              {/* Iter 50 — Measurement station annotations (sibling DOM
                  overlay, null when no alignment on the sheet). */}
              <MeasurementStationOverlay
                sheetId={sheet?.id}
                reloadKey={measurementsReloadKey}
              />
              {imgDims && (
                <RegionBoxesLayer
                  regions={scaleRegions.regions}
                  draftBbox={pendingRegionBbox}
                  imgDims={imgDims}
                  viewScale={scale}
                />
              )}
              {measureMode !== "off" && (
                <MeasureSvgLayer
                  points={measurePts}
                  hoverPoint={snappedHover}
                  scale={liveScale}
                  calibrationMode={measureMode === "calibrate"}
                  areaMode={toolMode === "area" && measureMode === "measure"}
                  polylineMode={toolMode === "polyline" && measureMode === "measure"}
                  coordinateMode={toolMode === "coordinate" && measureMode === "measure"}
                  radiusMode={toolMode === "radius" && measureMode === "measure"}
                  diameterMode={toolMode === "diameter" && measureMode === "measure"}
                  angleMode={toolMode === "angle" && measureMode === "measure"}
                  arcMode={toolMode === "arc" && measureMode === "measure"}
                  countMode={toolMode === "count" && measureMode === "measure"}
                  lengthUnit={measure.lengthUnit}
                  imgDims={imgDims}
                  viewScale={scale}
                />
              )}
              {/* Iter 145 — CAD snap indicator. Mounts inside the same
                  zoom/pan transform as the measure SVG so the ring sits
                  exactly under the snap target at every viewport scale. */}
              {measureMode !== "off" && currentSnap && (
                <SnapIndicator
                  snap={currentSnap}
                  imgDims={imgDims}
                  viewScale={scale}
                />
              )}
              {cropMode && (
                <CropSelectionLayer
                  rect={cropRect}
                  imgDims={imgDims}
                  viewScale={scale}
                  active={cropMode}
                />
              )}
              {(markupMode || markupState.markups.length > 0) && (
                <MarkupSvgLayer
                  markups={markupState.markups}
                  imgDims={imgDims}
                  viewScale={scale}
                  inProgress={markupInteraction.inProgress}
                />
              )}
            </>
          )}
          renderPin={(p) => {
            // Track 4 — asset markers route to the asset renderer.
            if (p && p.id?.startsWith?.("m_asset_")) {
              return assetMarkers.renderPin(p);
            }
            // Track 1 — Sheet Overlay Markers route through their own
            // renderer when the pin id is an overlay marker.
            if (p && (p._kind || p.id?.startsWith?.("m_") || p.id?.startsWith?.("cl_"))) {
              return overlayMarkers.renderPin(p);
            }
            const cat = PIN_CATEGORY[p.category] || {};
            const isActive = activePin === p.id;
            return (
              <div className="relative">
                <button
                  type="button"
                  className={`pin-marker ${fieldMode ? "pin-marker-field" : ""} ${isActive ? "active" : ""} ${p._pending_sync ? "pin-pending" : ""}`}
                  style={{ background: cat.color || "var(--fop-primary)", left: 0, top: 0 }}
                  title={p.title}
                  data-testid={`pin-marker-${p.id}`}
                />
                {isActive && (
                  <div
                    className="absolute z-30"
                    style={{ left: "100%", top: "0", marginLeft: 8 }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onWheel={(e) => e.stopPropagation()}
                  >
                    <PinQuickActions
                      pin={p}
                      onChanged={reload}
                      onDeleted={() => setActivePin(null)}
                      onClose={() => setActivePin(null)}
                    />
                  </div>
                )}
              </div>
            );
          }}
        />

        {/* Right inspector — Iter 73 Phase 2/3/4/9 tabbed surface.
            Hard-Gate visual cert (2026-09-07): below `lg` this was an
            in-flow grid column eating ~300px of a ~390px phone viewport,
            squeezing the plan into an unusable sliver. It now renders as
            a bottom drawer (with backdrop, tap-to-close) on phone/tablet,
            and reverts to the original in-flow right-hand panel at `lg`+. */}
        {!fieldMode && sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            data-testid="inspector-drawer-backdrop"
            onClick={toggleSidebar}
          />
        )}
        {!fieldMode && sidebarOpen && (
          <div
            className="fixed inset-x-0 bottom-0 z-40 h-[80vh] max-h-[80vh] rounded-t-2xl shadow-2xl overflow-hidden flex flex-col lg:static lg:inset-auto lg:z-auto lg:h-full lg:max-h-none lg:rounded-none lg:shadow-none"
            data-testid="inspector-drawer-panel"
          >
            <button
              type="button"
              onClick={toggleSidebar}
              className="lg:hidden flex items-center justify-center py-2 shrink-0"
              aria-label="Close inspector"
              data-testid="inspector-drawer-close"
            >
              <span className="w-10 h-1.5 rounded-full bg-[var(--fop-border-2)]" />
            </button>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <SheetInspectorPanel
                projectId={sheet?.project_id}
                sheetId={sheet?.id}
                tenantId={user?.tenant_id}
                pins={pins}
                activePin={activePin}
                setActivePin={setActivePin}
                PIN_CATEGORY={PIN_CATEGORY}
                recentList={recentList}
                onOpenSheet={(id) => navigate(`/sheet/${id}`)}
                StatusBadge={StatusBadge}
                PriorityBadge={PriorityBadge}
                markupState={markupState}
                onMeasurementsChanged={measure.bumpMeasurementsReloadKey}
              />
            </div>
          </div>
        )}
      </div>
      {/* Track 1 — Overlay marker detail drawer (Daylight + Field Black). */}
      {overlayMarkers.drawer}
      {assetMarkers.drawer}
      {assetMarkers.placementBanner}
      <AssetToolPalette
        open={assetPaletteOpen}
        onClose={() => setAssetPaletteOpen(false)}
        placementType={assetMarkers.placementType}
        setPlacementType={(t) => { assetMarkers.setPlacementType(t); setAssetPaletteOpen(false); }}
      />

      {/* Iter 73 Phase 2 — Bottom status bar (hidden in Field Mode) */}
      {!fieldMode && (
        <SheetStatusBar
          projectId={sheet?.project_id}
          sheetId={sheet?.id}
          markupsCount={markupState.markups.length}
          pinsCount={pins.length}
          zoomPct={Math.round((scaleHint || 1) * 100)}
          online={online}
          measurementsReloadKey={measurementsReloadKey}
        />
      )}

      {/* Iter 75 Phase 2 — Elite Object Card slide-over */}
      <ObjectCardOverlay tenantId={user?.tenant_id} />

      {/* Iter 75 Phase 4 — Offline queue visibility */}
      <OfflineQueuePanel online={online} />

      {/* Iter 78 — Raster health banner (only shows when raster is missing or blank) */}
      <RasterHealthBanner sheetId={sheet?.id} />

      {/* Iter 76 — Share modal */}
      {shareOpen && (
        <ShareModal
          projectId={sheet?.project_id}
          sheetId={sheet?.id}
          viewState={{ sheet_id: sheet?.id, zoom: scaleHint || 1 }}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* GH-31 (real-plan audit, 2026-10-01) — Scale Trust Gate recovery.
          The backend refuses to save a measurement on an NTS (Not To
          Scale) sheet with a 409 unless the operator explicitly
          acknowledges the risk. This is that reachable acknowledgment —
          previously there was none, so the measurement could never be
          saved on an NTS sheet at all. */}
      <AlertDialog open={!!measure.ntsPrompt} onOpenChange={(o) => { if (!o) measure.resolveNtsPrompt(false); }}>
        <AlertDialogContent data-testid="nts-acknowledge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[var(--fop-danger)]" />
              Not To Scale
            </AlertDialogTitle>
            <AlertDialogDescription>
              {measure.ntsPrompt?.message || "This sheet is classified NOT TO SCALE — graphical measurements on it are not reliable."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="nts-acknowledge-cancel" onClick={() => measure.resolveNtsPrompt(false)}>
              Discard
            </AlertDialogCancel>
            <AlertDialogAction data-testid="nts-acknowledge-confirm" onClick={() => measure.resolveNtsPrompt(true)}>
              Acknowledge &amp; Save Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 80 — Plan Export modal */}
      <PlanExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        projectId={sheet?.project_id}
        sheets={allSheets.length ? allSheets : (sheet ? [sheet] : [])}
        currentSheetId={sheet?.id}
      />

      {/* Quick Pin chip strip — appears when in Add Mode */}
      {addMode && (
        <QuickPinChips
          onPick={(tpl) => setTemplate(tpl)}
          onCustom={() => setTemplate(null)}
          active={template}
        />
      )}

      {/* New pin form (when no template was picked → typed entry) */}
      {newPinPos && !template && (
        <NewPinModal
          onCancel={() => setNewPinPos(null)}
          onSubmit={(data) => submitPin({ ...data, x: newPinPos.x, y: newPinPos.y })}
        />
      )}

      {/* (Legacy PinDetails bottom-right panel retired — inline PinQuickActions
          popover now anchors to the pin marker inside the canvas.) */}

      {/* Crop tool controls */}
      <CropControls
        active={cropMode}
        rect={cropRect}
        sheet={sheet}
        rasterUrl={rasterUrl}
        onClose={closeCrop}
        onReset={() => setCropRect(null)}
        includePins={cropIncludePins}
        setIncludePins={setCropIncludePins}
        includeMeasurements={cropIncludeMeasurements}
        setIncludeMeasurements={setCropIncludeMeasurements}
        pins={pins}
        onPinPhotoAttached={async (pinId) => {
          // Refresh the pin so the new photo shows up in the inline detail card.
          try {
            const r = await api.get(`/pins/${pinId}`);
            setPins((prev) => {
              const found = prev.find((p) => p.id === pinId);
              if (found) return prev.map((p) => p.id === pinId ? r.data : p);
              return [...prev, r.data];
            });
            setActivePin(pinId);
          } catch {/* non-essential */}
        }}
        renderOverlayToBlob={async (ctx, opts) => {
          // Project normalized image coords → cropped output canvas coords.
          const project = (nx, ny) => {
            const sxRel = (nx * (sheet ? 1 : 1)); // we use natural-image space; opts.sw/sh handle scaling
            const sxAbs = nx; // normalized within full image
            const xAbs = sxAbs * (opts.sx + opts.sw * (1)); // unused — keeping safe-default
            return null;
          };
          // We have the natural image scaled to canvas.width/.height. The selection
          // covers image coords [opts.sx, opts.sx+opts.sw] × [opts.sy, opts.sy+opts.sh].
          // So to translate from normalized → canvas-pixel:
          //   cx = (nx * imgNaturalW - opts.sx) * (canvas.width / opts.sw)
          // We have access to opts.sw and dw, dh. Compute naturalW = opts.sx + opts.sw... we
          // don't have natural dims here directly. The math simplifies if we trust that
          // opts.dw == opts.sw scaled to canvas.width 1:1 — which is what we do.
          const sxN = (nx) => ((nx * (opts.sx + opts.sw) - opts.sx) * (opts.dw / opts.sw));
          const syN = (ny) => ((ny * (opts.sy + opts.sh) - opts.sy) * (opts.dh / opts.sh));
          // Simpler/correct formulation: nat = sx + sw means full-image is implied if nx∈[0..1].
          // But our overlay points are also in 0..1 of the FULL image, so:
          const projN = (nx) => ((nx * (opts.sx + opts.sw) - opts.sx) * (opts.dw / opts.sw));
          const projNY = (ny) => ((ny * (opts.sy + opts.sh) - opts.sy) * (opts.dh / opts.sh));
          // Pins.
          if (opts.includePins) {
            for (const p of pins) {
              const cx = projN(p.x || 0);
              const cy = projNY(p.y || 0);
              if (cx < -10 || cy < -10 || cx > opts.dw + 10 || cy > opts.dh + 10) continue;
              const cat = PIN_CATEGORY[p.category] || {};
              ctx.beginPath();
              ctx.arc(cx, cy, 10, 0, 2 * Math.PI);
              ctx.fillStyle = cat.color || "#e85d1f";
              ctx.strokeStyle = "white";
              ctx.lineWidth = 2.5;
              ctx.fill();
              ctx.stroke();
            }
          }
          // Measurements (distance line OR area polygon if drawn).
          if (opts.includeMeasurements && measurePts.length >= 2) {
            ctx.strokeStyle = "#e85d1f";
            ctx.lineWidth = 3;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.beginPath();
            measurePts.forEach((pt, i) => {
              const cx = projN(pt.nx);
              const cy = projNY(pt.ny);
              if (i === 0) ctx.moveTo(cx, cy); else ctx.lineTo(cx, cy);
            });
            if (toolMode === "area" && measurePts.length >= 3) {
              ctx.closePath();
              ctx.fillStyle = "rgba(232, 93, 31,0.10)";
              ctx.fill();
            }
            ctx.stroke();
            // Endpoint dots.
            ctx.fillStyle = "#e85d1f";
            measurePts.forEach((pt) => {
              ctx.beginPath();
              ctx.arc(projN(pt.nx), projNY(pt.ny), 5, 0, 2 * Math.PI);
              ctx.fill();
            });
          }
        }}
      />

      {/* Measure tool controls (bottom of viewer) */}
      <MeasureControls
        active={measureMode !== "off"}
        onClose={closeMeasure}
        toolMode={toolMode}
        onSetToolMode={setToolMode}
        points={measurePts}
        hoverPoint={measureHover}
        scale={(measureMode === "calibrate" || measureMode === "region_box") ? sheetScale : liveScale}
        gpsReady={!!gpsTransform?.sheetToGps}
        calibrationMode={measureMode === "calibrate"}
        regionBoxMode={measureMode === "region_box"}
        onStartCalibration={startCalibrateExclusive}
        onStartMeasure={startMeasureExclusive}
        onClearPoints={clearPoints}
        onUndoPoint={undoMeasurePoint}
        onCalibrateSave={async (payload) => { await saveSheetScale(payload); }}
        onClearScale={async () => { await clearSheetScale(); }}
        onFinishCount={measure.finishCount}
        imgDims={viewerImgDims}
        regionBbox={pendingRegionBbox}
        onRegionCalibrateSave={onRegionCalibrateSave}
        onCancelRegion={onCancelRegion}
        onStartRegionBox={startRegionBoxExclusive}
        regions={scaleRegions.regions}
        onRenameRegion={async (id, label) => { await scaleRegions.update(id, { region_label: label }); }}
        onDeleteRegion={async (id) => { await scaleRegions.remove(id); }}
        scaleContext={sheet?.scale_context}
      />

      {/* Wave 5 (2026-09-11) — Earthwork (Average End Area) estimator. */}
      {earthworkOpen && sheet?.project_id && (
        <EarthworkEstimatePanel
          projectId={sheet.project_id}
          onClose={() => setEarthworkOpen(false)}
        />
      )}


      {/* PILLAR 2 — Measurement History Drawer. */}
      {historyOpen && sheet?.id && (
        <MeasurementHistoryDrawer
          sheetId={sheet.id}
          reloadKey={measurementsReloadKey}
          onClose={() => setHistoryOpen(false)}
          onDeleted={measure.bumpMeasurementsReloadKey}
          onRecenter={(m) => {
            try {
              const pt = (m.points && m.points[0]) || null;
              if (pt && typeof pt.nx === "number" && typeof pt.ny === "number") {
                // R11 continuation (2026-09-10) — this call had the wrong
                // argument shape (`dispatchCanvasCmd("focus_point", {...})`
                // passes a string as targetId and an object as `action`,
                // which SheetCanvas's listener silently ignores since
                // `target !== sheetId`). "Recenter on this measurement"
                // has been a no-op since it was written. Correct
                // signature: (sheetId, "centerOn", { nx, ny }).
                dispatchCanvasCmd(sheetId, "centerOn", { nx: pt.nx, ny: pt.ny });
              }
            } catch {/* swallow — recenter is best-effort */}
          }}
        />
      )}

      {/* Iter 144 Phase 2 — precision magnifier. Iter 146: disabled on
          touch-primary devices (iPad/iPhone) — field test showed the
          loupe flickered during rapid touch movement and contributed
          to the operator's "glitchy" verdict. Desktop / Apple-Pencil
          hover users still get the loupe.
          TRACK 1 — On touch the loupe is gated behind a 250ms still
          finger hold (precisionHold), eliminating the flicker source
          and restoring the precision aid Bluebeam offers. */}
      <MeasureMagnifier
        rasterUrl={rasterUrl}
        hoverPoint={snappedHover}
        screenPos={measureScreenPos}
        active={measureMode !== "off" && (snapEnabled || precisionHold)}
      />

      {/* PILLAR 2A — Touch Target Crosshair.
          When the operator is measuring on touch, the SheetCanvas applies
          a 56px Y-offset so the target is ABOVE the finger. This crosshair
          renders AT that offset position so the operator sees exactly
          where the next tap commits. The finger never covers it.

          Only rendered for touch interactions — desktop hover already
          shows the rubber-band-with-cursor pattern. */}
      {measureMode !== "off" && measureScreenPos?.isTouch && (
        <div
          className="pointer-events-none fixed z-[72]"
          data-testid="touch-target-crosshair"
          style={{
            left: measureScreenPos.x,
            top: measureScreenPos.y,
            transform: "translate(-50%, -50%)",
          }}
        >
          {/* outer halo ring */}
          <div
            style={{
              position: "absolute",
              left: "50%", top: "50%",
              width: 36, height: 36,
              transform: "translate(-50%, -50%)",
              border: "2px solid rgba(255,255,255,0.9)",
              borderRadius: "50%",
              boxShadow: "0 0 8px rgba(0,0,0,0.45)",
            }}
          />
          {/* primary ring */}
          <div
            style={{
              position: "absolute",
              left: "50%", top: "50%",
              width: 28, height: 28,
              transform: "translate(-50%, -50%)",
              border: "2px solid var(--fop-primary)",
              borderRadius: "50%",
            }}
          />
          {/* crosshair lines (white halo + primary inner) */}
          <div style={{ position: "absolute", left: "50%", top: "50%", width: 2, height: 22, transform: "translate(-50%, -50%)", background: "var(--fop-primary)", boxShadow: "0 0 0 1px rgba(255,255,255,0.9)" }} />
          <div style={{ position: "absolute", left: "50%", top: "50%", width: 22, height: 2, transform: "translate(-50%, -50%)", background: "var(--fop-primary)", boxShadow: "0 0 0 1px rgba(255,255,255,0.9)" }} />
          {/* leader line from target down to the finger so the operator
              connects the crosshair to their thumb at first glance. */}
          <div
            style={{
              position: "absolute",
              left: "50%", top: "50%",
              width: 2, height: 38,
              transform: "translate(-50%, 4px)",
              background: "linear-gradient(to bottom, var(--fop-primary), rgba(255,107,53,0.0))",
              opacity: 0.85,
            }}
          />
        </div>
      )}

      {/* Iter 145 — floating CAD snap label near the finger.
          Reads the screen position from the magnifier-feed and offsets the
          label so it sits ABOVE the loupe (or below if the loupe flipped
          down). The label is the operator's "I am snapped" confirmation. */}
      {measureMode !== "off" && currentSnap && measureScreenPos && (
        <div
          className="pointer-events-none fixed z-[71] px-3 min-h-[32px] inline-flex items-center rounded-full bg-[var(--fop-primary)] text-black shadow-lg font-mono text-[11px] uppercase tracking-wider font-bold"
          data-testid="snap-label"
          data-snap-kind={currentSnap.kind}
          style={{
            left: Math.max(8, Math.min((typeof window !== "undefined" ? window.innerWidth : 1024) - 200, measureScreenPos.x + 16)),
            top: Math.max(8, measureScreenPos.y - 44),
          }}
        >
          {currentSnap.label}
        </div>
      )}

      {/* Markup tool controls + text entry */}
      <MarkupReadBanner readState={markupState.readState} onRetry={markupState.retry} />
      <FailedDraftsBanner
        drafts={measure.failedDrafts}
        onRetry={measure.retryFailedDraft}
        onDiscard={measure.discardFailedDraft}
        onCopy={measure.copyFailedDraft}
      />
      <MarkupControls
        active={markupMode}
        tool={markupState.tool}
        color={markupState.color}
        onSetTool={markupState.setTool}
        onSetColor={markupState.setColor}
        onClearAll={markupState.clearAll}
        onUndo={markupState.undoLast}
        count={markupState.markups.length}
        onClose={closeMarkup}
        inProgress={markupInteraction.inProgress}
      />
      <TextEntryPopover
        open={!!markupInteraction.textPending}
        onSubmit={markupInteraction.submitText}
        onCancel={markupInteraction.cancelText}
      />

      {/* GPS panel — advanced calibration UI (anchors, lat/lng entry). */}
      {gpsPanelOpen && (
        <GpsPanel
          sheetId={sheet.id}
          sheetNumber={sheet.sheet_number}
          gpsEnabled={gpsEnabled}
          onToggle={toggleGpsLive}
          cal={cal}
          onSaveCalibration={saveCalibration}
          onClearCalibration={clearCalibration}
          livePosition={livePosition}
          liveError={liveError}
          manualPosition={manualPosition}
          onClearManual={() => setManualPosition(null)}
          awaitingAnchorTap={awaitingAnchorTap}
          onAwaitAnchorTap={() => { if (awaitingAnchorTap) setAwaitingAnchorTap(false); else armAnchorTap(); }}
          anchorTapPos={anchorTapPos}
          onCancelAnchorTap={() => { setAnchorTapPos(null); setAwaitingAnchorTap(false); }}
          onClose={() => setGpsPanelOpen(false)}
        />
      )}

      {/* Iter 178.3 — Floating "Where am I?" pill deleted. It now lives
          inline inside the trust strip (see the trust-strip block above)
          so it stops floating over the drawing. Modal + state below
          remain unchanged so every trigger — trust-strip button, keyboard
          shortcut, or programmatic — still opens the same overlay. */}
      {sheet && projectLocateOpen && (
        <ProjectLocate
          open={projectLocateOpen}
          onClose={() => setProjectLocateOpen(false)}
          projectId={sheet.project_id}
          sheetId={sheet.id}
          sheetNumber={sheet.sheet_number}
          onSwitchSheet={({ sheetId: targetId }) => {
            if (targetId && targetId !== sheet.id) navigate(`/sheet/${targetId}`);
          }}
          onPosition={(pos) => {
            // Iter FIELD-UX-2 — funnel I'm Here arm through the tool
            // ownership model so it can never coexist with measure /
            // markup / addPin etc.
            if (pos?.enterManualMode === true) { armImHere(); return; }
            if (pos?.enterManualMode === false) { setImHereMode(false); return; }
            if (pos) {
              setProjectPosition(pos);
              setImHereMode(false);
            }
          }}
        />
      )}

      {/* Iter 34 — LocateControl: production-grade Locate/Follow/Drop cluster.
          Iter 146 — also hidden during active measurement so the right-side
          drawing area stays clear for measure-tool work. Field workers
          measure and locate as distinct tasks; never both at once. */}
      {/* Legacy LocateControl — desktop PMs (≥lg viewports) only. On iPad
          portrait + iPhone the SC-4 "Where am I?" pill is the single
          obvious action, per stabilization audit. */}
      {sheet && !fieldMode && measureMode === "off" && (
        <div className="hidden lg:block">
          <LocateControl
          gpsEnabled={gpsEnabled}
          ready={gpsReady}
          livePosition={livePosition}
          liveError={liveError}
          followMode={followMode}
          onToggleFollow={handleToggleFollow}
          onLocateMe={handleLocateMe}
          dropMode={dropMode}
          onToggleDropMode={() => { if (dropMode) { if (typeof setDropMode === "function") setDropMode(false); } else { armDropMode(); } }}
          hasManual={!!manualPosition}
          onClearManual={() => setManualPosition(null)}
          onOpenCalibration={() => setGpsPanelOpen(true)}
          inspectorOpen={!!inspector?.state}
        />
        </div>
      )}

      {/* Sheet-jump command palette */}
      {jumpOpen && (
        <SheetJumpOverlay
          projectId={sheet.project_id}
          currentSheetId={sheet.id}
          onNavigate={(id) => { setJumpOpen(false); navigate(`/sheet/${id}`); }}
          onClose={() => setJumpOpen(false)}
        />
      )}

      {/* Field Mode FAB + quick switcher */}
      {fieldMode && (
        <>
          <button
            type="button"
            onClick={() => { if (addMode) { setAddMode(false); } else { armAddPin(); } setNewPinPos(null); setTemplate(null); }}
            className={`fixed z-30 rounded-full shadow-2xl flex items-center justify-center transition-all ${
              addMode ? "bg-[var(--st-escalated)] text-white w-16 h-16" : "bg-[var(--fop-primary)] text-[var(--fop-bg)] w-20 h-20 hover:scale-105"
            }`}
            style={{
              // Iter 144 — respect iPad home indicator + bottom safe-area
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)",
              right: "calc(env(safe-area-inset-right, 0px) + 2rem)",
            }}
            data-testid="add-pin-fab"
            aria-label={addMode ? "Cancel" : "Drop Pin"}
            title={addMode ? "Cancel (Esc)" : "Drop Pin (P)"}
          >
            {addMode ? <X size={28} /> : <Plus size={32} />}
          </button>

          {recentList.length > 0 && !addMode && (
            <div
              className="fixed z-30 flex items-center gap-2 px-3 py-2 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border)] shadow-2xl"
              style={{
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)",
                left: "calc(env(safe-area-inset-left, 0px) + 2rem)",
              }}
              data-testid="field-recent-switcher"
            >
              {recentList.slice(0, 4).map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/sheet/${r.id}`)}
                  className="min-h-[44px] min-w-[44px] px-3 rounded-full font-mono text-sm font-bold text-[var(--fop-text)] bg-[var(--fop-surface)] hover:bg-[var(--fop-primary)] hover:text-[var(--fop-bg)] transition-colors"
                  title={`${r.sheet_number} · ${r.sheet_title}`}
                  data-testid={`field-recent-${r.sheet_number}`}
                >
                  {r.sheet_number}
                </button>
              ))}
            </div>
          )}

          {/* Quick Pin mode minimal exit pill */}
          <button
            onClick={toggleFieldMode}
            className="fixed z-30 inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] font-mono text-xs uppercase tracking-wider"
            style={{
              top: "calc(env(safe-area-inset-top, 0px) + 1rem)",
              left: "calc(env(safe-area-inset-left, 0px) + 1rem)",
            }}
            data-testid="field-exit"
            title="Exit Field Mode (F)"
          >
            <Smartphone size={14} /> Exit Field
          </button>

          {/* GH-12 (real-plan certification, 2026-09-30) — Field Mode
              hid the ENTIRE topbar (back/plan-list) and the ENTIRE tool
              rail (Measure, Markup) with no touch-reachable replacement
              — only a keyboard shortcut or the small "Exit Field" pill
              could get them back. This compact, always-visible cluster
              keeps Plan list / Measure / Markup reachable by touch
              without bringing back the full desktop chrome (field mode's
              whole point — maximum canvas space — is preserved). */}
          <div
            className="fixed z-30 flex items-center gap-2 px-2 py-2 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border)] shadow-2xl"
            style={{
              top: "calc(env(safe-area-inset-top, 0px) + 4.25rem)",
              left: "calc(env(safe-area-inset-left, 0px) + 1rem)",
            }}
            data-testid="field-action-bar"
          >
            <button
              onClick={() => navigate(sheet.project_id ? `/project/${sheet.project_id}/plans` : "/portfolio")}
              className="flex items-center justify-center rounded-full text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)]"
              style={{ minHeight: 44, minWidth: 44 }}
              data-testid="field-back-to-plans"
              aria-label="Back to plan list"
              title="Back to plan list"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              onClick={() => { if (measureMode === "off") startMeasureExclusive("distance"); else closeMeasure(); }}
              className={`flex items-center gap-1.5 px-3 rounded-full font-mono text-[11px] font-bold uppercase tracking-wider ${
                measureMode !== "off" ? "bg-[var(--fop-primary)] text-[var(--fop-bg)]" : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)]"
              }`}
              style={{ minHeight: 44 }}
              data-testid="field-measure"
              aria-label="Measure"
              title="Measure"
            >
              <Ruler size={16} /> Measure
            </button>
            <button
              onClick={openMarkup}
              className={`flex items-center gap-1.5 px-3 rounded-full font-mono text-[11px] font-bold uppercase tracking-wider ${
                markupMode ? "bg-[var(--fop-primary)] text-[var(--fop-bg)]" : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)]"
              }`}
              style={{ minHeight: 44 }}
              data-testid="field-markup"
              aria-label="Markup"
              title="Markup"
            >
              <PenSquare size={16} /> Markup
            </button>
          </div>

          {/* Field-mode sheet identity (tiny corner pill so a super always knows what they're looking at) */}
          <div
            className="fixed z-30 inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border)]"
            style={{
              top: "calc(env(safe-area-inset-top, 0px) + 1rem)",
              right: "calc(env(safe-area-inset-right, 0px) + 1rem)",
            }}
            data-testid="field-sheet-id"
          >
            <div className="font-mono text-sm font-bold text-[var(--fop-text)]">{sheet.sheet_number}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">REV {sheet.revision}</div>
          </div>
        </>
      )}
      {/* Inspector slot — empty today, P1 plugs in here without touching SheetViewer. */}
      <InspectorBridge
        review={activeReview}
        myAck={myAck}
        onAckResolved={onAckResolved}
        distanceFromMe={distanceFromMe}
        sheetId={sheet?.id}
        gpsContext={
          livePosition && gpsTransform
            ? { lat: livePosition.lat, lon: livePosition.lng, accuracy: livePosition.accuracy }
            : null
        }
      />
      {/* Iter 37 — Ephemeral crossing inspector popover. Renamed
          Iter 63.1 to disambiguate from the persistent
          components/object/ConflictInspector (a PROMOTED crossing). */}
      <EphemeralCrossingInspector
        crossing={activeCrossing}
        inspectorOpen={!!inspector?.state}
        onClose={() => setActiveCrossing(null)}
        onFocusStructure={(s) => {
          inspector.open({ kind: "structure", id: s.id, payload: s });
          setActiveCrossing(null);
        }}
      />

      {/* Iter 38 — Operational Status Spine. Thin tactical strip at the
          bottom giving glanceable awareness without leaving the drawing.
          Hidden in Field Mode (FAB+swipe own the field gesture set). */}
      {!fieldMode && sheet && (
        <OperationalStatusSpine
          sheet={sheet}
          sheetPosition={sheetPosition}
          ackSummary={ackSummary}
          activeReview={activeReview}
          utilityIntelVisible={utilityIntelVisible}
          onTapReviews={focusFirstImpact}
          onTapAcks={focusFirstImpact}
          onTapUtility={({ topCrossing }) => {
            if (topCrossing) {
              if (!utilityIntelVisible) {
                setUtilityIntelVisible(true);
                writeUtilityIntelVisible(true);
              }
              setActiveCrossing(topCrossing);
            }
          }}
        />
      )}
    </div>
  );
}

// R-10 (independent recheck, 2026-10-01) — persistent (not toast-timed-out)
// banner for measurements that failed BOTH the network save and the
// offline durable queue. `drafts` comes straight from
// `useMeasurementController`'s localStorage-backed `failedDrafts`, so it
// is still here after a navigate-away/navigate-back or a page reload —
// the operator never has to redraw a lost measurement from memory.
function FailedDraftsBanner({ drafts, onRetry, onDiscard, onCopy }) {
  if (!drafts || drafts.length === 0) return null;
  return (
    <div
      className="fixed z-[70] left-1/2 -translate-x-1/2 top-[112px] flex flex-col gap-1.5 max-w-[92vw]"
      data-testid="failed-draft-banner"
      data-draft-count={drafts.length}
    >
      {drafts.map((d) => (
        <div
          key={d.id}
          className="flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg bg-[var(--fop-danger)] text-white font-mono text-[11px] uppercase tracking-wider font-bold"
          data-testid="failed-draft-row"
          data-draft-id={d.id}
        >
          <AlertTriangle size={14} className="shrink-0" />
          <span className="truncate">
            Not saved · {d.payload?.kind || "measurement"} · {d.payload?.label || `${(d.payload?.points || []).length} pts`}
            {/* B2-03 — honest persistence status: don't imply a reload-
               proof guarantee that isn't real when even localStorage
               rejected the write and only this tab's memory holds it. */}
            {d.persisted === false && (
              <span data-testid="failed-draft-session-only" className="ml-1 opacity-90">
                · session only, save or copy before closing this tab
              </span>
            )}
          </span>
          <button
            onClick={() => onRetry(d.id)}
            className="ml-1 flex items-center gap-1 px-2 py-0.5 rounded bg-black/20 hover:bg-black/30 shrink-0"
            data-testid="failed-draft-retry"
          >
            <RefreshCw size={11} /> Retry
          </button>
          <button
            onClick={async () => {
              const ok = await onCopy(d.id);
              toast[ok ? "success" : "error"](ok ? "Draft copied" : "Couldn't copy — clipboard unavailable");
            }}
            className="flex items-center gap-1 px-2 py-0.5 rounded bg-black/20 hover:bg-black/30 shrink-0"
            data-testid="failed-draft-copy"
          >
            <Copy size={11} /> Copy
          </button>
          <button
            onClick={() => onDiscard(d.id)}
            className="flex items-center gap-1 px-2 py-0.5 rounded bg-black/20 hover:bg-black/30 shrink-0"
            data-testid="failed-draft-discard"
          >
            <X size={11} /> Discard
          </button>
        </div>
      ))}
    </div>
  );
}


function QuickPinChips({ onPick, onCustom, active }) {
  return (
    <div className="absolute left-0 right-0 bottom-0 z-20 px-4 pt-3 pb-4 bg-gradient-to-t from-[#0a0a0b]/95 to-transparent" data-testid="quick-pin-chips">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2">
        Drop a pin — tap a template, then tap the drawing
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={onCustom}
          className={`px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${
            !active ? "border-[var(--fop-primary)] bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]" : "border-[var(--fop-border)] text-[var(--fop-text-2)] hover:border-[var(--fop-text-3)]"
          }`}
          data-testid="quickpin-custom"
        >
          Custom
        </button>
        {/* Iter 67 Phase 5 — Recent templates first (persisted in
            localStorage; most-recently-used left-most). */}
        {(() => {
          const { recent, rest } = partitionTemplatesByRecent();
          const ordered = [...recent, ...rest];
          return ordered.map((tpl) => {
            const cat = PIN_CATEGORY[tpl.category] || {};
            const selected = active?.id === tpl.id;
            const isRecent = recent.includes(tpl);
            return (
              <button
                key={tpl.id}
                onClick={() => { recordTemplateUse(tpl.id); onPick(tpl); }}
                className={`px-3 py-1.5 rounded-full text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                  selected
                    ? "border-[var(--fop-primary)] bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
                    : "border-[var(--fop-border)] text-[var(--fop-text-2)] hover:border-[var(--fop-text-3)]"
                }`}
                data-testid={`quickpin-${tpl.id}`}
                title={tpl.title}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                {tpl.label}
                {isRecent && (
                  <span className="text-[8px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] -mr-1">
                    ↺
                  </span>
                )}
              </button>
            );
          });
        })()}
      </div>
    </div>
  );
}

function RecentSheetsStrip({ recents, onOpen }) {
  if (!recents || recents.length === 0) return null;
  return (
    <div className="border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)]" data-testid="recent-sheets-strip">
      <div className="px-4 pt-3 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
        Recently viewed
      </div>
      <ul className="px-3 pb-3 space-y-1.5">
        {recents.slice(0, 4).map((r) => (
          <li key={r.id}>
            <button
              onClick={() => onOpen(r.id)}
              className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-[var(--fop-surface)] transition-colors group text-left"
              data-testid={`recent-sheet-${r.sheet_number}`}
            >
              <img
                src={sheetThumbUrl(r.id)}
                alt=""
                className="w-12 h-10 rounded border border-[var(--fop-border-2)] object-cover"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs font-bold text-[var(--fop-text)] group-hover:text-[var(--fop-primary)] truncate">
                  {r.sheet_number}
                </div>
                <div className="text-[10px] text-[var(--fop-text-2)] truncate leading-tight">
                  {r.sheet_title}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewPinModal({ onCancel, onSubmit }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("field_issue");
  const [priority, setPriority] = useState("medium");
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onCancel} data-testid="new-pin-modal">
      <div
        className="w-full max-w-sm m-4 bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">New pin</div>
        <div className="font-display text-lg text-[var(--fop-text)] mb-4">What did you find?</div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim()) return;
            onSubmit({ title: title.trim(), category, priority });
          }}
          className="space-y-3"
        >
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief description"
            className="w-full"
            data-testid="newpin-title"
          />
          <div className="grid grid-cols-2 gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-sm" data-testid="newpin-category">
              {Object.entries(PIN_CATEGORY).map(([key, c]) => (
                <option key={key} value={key}>{c.label}</option>
              ))}
            </select>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="text-sm" data-testid="newpin-priority">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onCancel} className="btn btn-ghost text-xs" data-testid="newpin-cancel">Cancel</button>
            <button type="submit" className="btn btn-primary text-xs" data-testid="newpin-submit">Drop pin</button>
          </div>
        </form>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ *
 * Iter 63.1 — first-time on-canvas hint.                             *
 *                                                                    *
 * Shown ONCE per user (localStorage flag). Construction-friendly      *
 * 1-line copy explaining the two key gestures. Dismiss with Got it    *
 * or any tap on the drawing.                                          *
 * ------------------------------------------------------------------ */
function FirstTimeViewerHint({ hidden = false }) {
  const KEY = "fop_viewer_hint_dismissed_v1";
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (hidden) return;
    try {
      const dismissed = window.localStorage.getItem(KEY);
      if (!dismissed) {
        const t = setTimeout(() => setShow(true), 800);
        // Iter 144 — auto-dismiss after 6s so the hint never lingers
        // as visual clutter on the field operator's screen.
        const auto = setTimeout(() => {
          setShow(false);
          try { window.localStorage.setItem(KEY, "1"); } catch {}
        }, 6800);
        return () => { clearTimeout(t); clearTimeout(auto); };
      }
    } catch { /* private mode */ }
  }, [hidden]);

  const dismiss = () => {
    setShow(false);
    try { window.localStorage.setItem(KEY, "1"); } catch {}
    trackViewerEvent("viewer.first_time_hint_dismissed");
  };

  if (!show || hidden) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-3 z-50 flex justify-center"
      data-testid="viewer-first-time-hint"
    >
      <div className="pointer-events-auto inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border-2)] shadow-lg text-sm text-[var(--fop-text)]">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-primary)]">Quick start</span>
        <span className="text-[var(--fop-text-2)]">Tap a structure to inspect it. Tap <b className="text-[var(--fop-text)]">Add Pin</b> to drop one.</span>
        <button
          onClick={dismiss}
          data-testid="viewer-first-time-hint-dismiss"
          className="ml-1 min-h-[44px] inline-flex items-center px-3 rounded-md text-xs font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Iter 63.1 — toolbar "More" overflow item.                          *
 * ------------------------------------------------------------------ */
function MoreItem({ active, onClick, icon: Icon, label, hint, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "1" : "0"}
      className={`w-full flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm transition-colors ${
        active
          ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
          : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
      }`}
    >
      <Icon size={16} />
      <span className="flex-1 text-left">{label}</span>
      {hint && (
        <kbd className="text-[11px] font-mono uppercase tracking-wider px-1 py-0.5 rounded border border-[var(--fop-border)] text-[var(--fop-text-4)]">
          {hint}
        </kbd>
      )}
    </button>
  );
}
